<template>
  <div class="channel-detail-page">
    <!-- 头部 -->
    <div class="channel-detail-header">
      <van-icon name="arrow-left" size="20" color="#4A4A4A" style="cursor: pointer;" @click="$router.push('/')" />
      <div class="channel-detail-title-wrapper" @click="goToMembers">
        <img v-if="channel.avatar" :src="$common.getFullImageUrl(channel.avatar, $config)" class="channel-header-avatar" />
        <div class="channel-detail-title-group">
          <span class="channel-detail-title">{{ channel.name || $t('channels_title') }}</span>
          <div v-if="channel.memberCnt" class="channel-detail-members">
            <van-icon name="friends-o" size="11" color="#8A8A8A" />
            <span>{{ channel.memberCnt }}</span>
          </div>
        </div>
      </div>
      <div style="display: flex; align-items: center; gap: 8px;">
        <van-icon name="search" size="20" color="#4A4A4A" style="cursor: pointer;" @click="goToSearch" />
        <van-icon name="ellipsis" size="20" color="#4A4A4A" style="cursor: pointer;" @click="showSettings = true" />
      </div>
    </div>

    <!-- 描述 Banner -->
    <div v-if="channel.description" class="channel-desc-banner">
      <van-icon name="star-o" size="13" color="#E8543A" style="flex-shrink: 0;" />
      <span class="channel-desc-text">{{ channel.description }}</span>
    </div>

    <!-- 广告：发布成功后落地本页，此处曝光价值高（对齐 client channel_detail_page） -->
    <AdBanner v-if="$config.region !== 'cn'" type="banner" page="channelDetail" />

    <!-- 会话列表 -->
    <div class="channel-sessions-wrapper" :style="{ height: sessionsWrapperHeight }">
      <div class="channel-sessions-header">
      </div>
      <div class="channel-sessions sessions-reverse" ref="sessionsContainer" @scroll="onSessionsScroll">

      <!-- column-reverse 下内容不足一屏时，该占位元素的 auto margin 吸收全部剩余空间，使消息顶部对齐 -->
      <div class="sessions-bottom-spacer"></div>

      <div v-if="loading && sessions.length === 0" style="display: flex; justify-content: center; padding: 40px;">
        <van-loading size="24" />
      </div>

      <div v-if="loading && sessions.length > 0" style="display: flex; justify-content: center; padding: 12px 0;">
        <van-loading size="20" />
      </div>

      <div v-if="sessions.length === 0 && joinMsgs.length > 0 && !loading && hasLoaded" class="channel-empty-state channel-empty-state-inline">
        <div class="channel-empty-icon">
          <div class="channel-empty-icon-bg">
            <img class="channel-empty-icon-logo" src="/static/logo-transparent.png" alt="logo" />
          </div>
        </div>
        <div class="channel-empty-title">{{ $t('channelEmpty_title') }}</div>
        <div class="channel-empty-subtitle">{{ $t('channelEmpty_subtitle') }}</div>
        <div class="channel-empty-hint">
          <van-icon name="star-o" size="10" color="#E8543A" class="channel-empty-hint-icon" />
          <span>{{ $t('channelEmpty_hint') }}</span>
        </div>
        <div v-if="channel.memberCnt" class="channel-empty-members">
          <van-icon name="friends-o" size="14" color="#999" />
          <span>{{ channel.memberCnt }} {{ $t('channel_detail_members') }}</span>
        </div>
      </div>

      <div v-for="session in feedItems" :key="session.chatSessionId || ('join-' + session.joinMsg.id)">
        <!-- 聚合进群消息 banner：轻量展示，无卡片/无点击交互 -->
        <div v-if="session.joinMsg" class="join-msg-banner">
          <!-- 只展示有真实头像的用户；没头像的不出占位首字母块，只保留在文案里 -->
          <div v-if="joinMsgAvatarUsers(session.joinMsg).length" class="join-msg-avatars">
            <img v-for="(joinUser, joinIdx) in joinMsgAvatarUsers(session.joinMsg)" :key="'ja' + joinIdx" :src="$common.getFullImageUrl(joinUser.avatar, $config, 's')" class="join-msg-avatar" />
          </div>
          <span class="join-msg-text">{{ joinMsgText(session.joinMsg) }}</span>
        </div>
        <div v-else class="session-item" :class="{ 'session-highlight': session.chatSessionId === targetSessionId && highlightTarget }" :data-session-id="session.chatSessionId">
        <div class="session-wrapper" :class="{ 'session-mine': isMySession(session) }">
          <!-- 头像（在卡片外部） -->
          <div class="session-bubble-avatar">
            <img v-if="session.fromUserAvatar" :src="$common.getFullImageUrl(session.fromUserAvatar, $config, 's')"
              class="session-bubble-avatar-img" />
            <div v-else class="session-bubble-avatar-placeholder">
              {{ (session.fromUserName || '?')[0] }}
            </div>
          </div>
          <!-- 主体：meta(卡片外小字) + 卡片 -->
          <div class="session-bubble-main" :class="{ 'session-bubble-main-mine': isMySession(session) }">
            <!-- 微信风格：用户名+时间在卡片外，小字 -->
            <div class="session-bubble-meta">
              <div class="session-bubble-meta-group">
                <span class="session-bubble-name">{{ session.fromUserName || $t('anonymous') }}</span>
                <span v-if="session.createdAt" class="session-bubble-time">{{ $common.formatSmartTime(session.createdAt, $i18n.locale) }}</span>
              </div>
              <!-- 快捷设置入口（仅自己的卡片）：直达 持续匹配/公开/允许直接联系 -->
              <van-icon v-if="isMySession(session)" name="setting-o" size="13" color="#8A8A8A" class="session-bubble-quick-settings"
                @click.stop="openQuickSettings(session)" />
            </div>
            <!-- 卡片（内容自适应宽度） -->
            <div class="session-card" @click="openSessionDetail(session)">
              <div class="session-bubble-content">
              <!-- 消息气泡 -->
              <div class="session-bubble-body">
                <!-- 私有消息（contentHidden 由后端统一判断） -->
                <div v-if="session.contentHidden" class="session-private-lock">
                  <van-icon name="lock" size="14" color="#E0DDD8" />
                  <span>{{ $t('session_messageIsPrivate') }}</span>
                </div>
                <template v-else>
                  <!-- 优先展示 userMsg（用户说的话，作为主内容） -->
                  <div v-if="session.userMsg && isUserMsgMixed(session.userMsg)" class="session-bubble-usermsg-main">
                    <div v-for="(item, idx) in parseMixedContent(session.userMsg)" :key="'umix-' + idx">
                      <div v-if="item.type == 'plaintext'" style="font-size: 14px; color: #1A1A1A; line-height: 1.5;">{{ item.content }}</div>
                      <div v-else-if="item.type == 'img'" style="margin-top: 6px;">
                        <img :src="$common.getFullImageUrl(item.content.url || '', $config)" class="session-bubble-media-img" />
                      </div>
                    </div>
                  </div>
                  <div v-else-if="session.userMsg" class="session-bubble-usermsg-main">{{ $common.formatUserMsgPreview(session.userMsg) }}</div>
                  <div v-else-if="session.aiSummary" class="session-bubble-summary">{{ session.aiSummary }}</div>
                  <!-- aiSummary 作为系统匹配摘要，弱化展示（不独立成框，紧贴 userMsg） -->
                  <div v-if="session.userMsg && session.aiSummary" class="session-bubble-ai-sub">
                    <span class="session-bubble-ai-sub-label">{{ $t('aiChat_matchSummaryLabel') }}</span>
                    <span class="session-bubble-ai-sub-text">{{ session.aiSummary }}</span>
                  </div>
                  <!-- 首次非文本内容展示（图片/位置等）；userMsg 为 mixed 时图片已在上方 user msg 区域展示，跳过避免重复（与 client 端逻辑一致） -->
                  <div v-if="parseFirstMediaContent(session.firstMediaContent) && !(session.userMsg && isUserMsgMixed(session.userMsg))" class="session-bubble-media">
                    <template v-if="parseFirstMediaContent(session.firstMediaContent).type === 'img'">
                      <img :src="$common.getFullImageUrl(parseFirstMediaContent(session.firstMediaContent).content.url || '', $config)" class="session-bubble-media-img" />
                    </template>
                    <template v-else-if="parseFirstMediaContent(session.firstMediaContent).type === 'pick_location'">
                      <div class="session-bubble-media-location">
                        <img :src="getStaticMapUrl(parseFirstMediaContent(session.firstMediaContent).content.lat, parseFirstMediaContent(session.firstMediaContent).content.lng)" class="session-bubble-media-map" />
                        <div class="session-bubble-media-location-info">
                          <van-icon name="location-o" size="10" color="#3DAECF" />
                          <span>{{ parseFirstMediaContent(session.firstMediaContent).content.address || '' }}</span>
                        </div>
                      </div>
                    </template>
                    <template v-else-if="parseFirstMediaContent(session.firstMediaContent).type === 'file'">
                      <van-icon name="description" size="12" color="#8A8A8A" />
                      <span class="session-bubble-media-text">{{ parseFirstMediaContent(session.firstMediaContent).content.name || parseFirstMediaContent(session.firstMediaContent).content.fileName || '' }}</span>
                    </template>
                  </div>
                  <!-- 最新2条评论预览（在气泡内部） -->
                  <div v-if="isMember && !(commentStates[session.chatSessionId] && commentStates[session.chatSessionId].expanded) && parseCommentPreviews(session.latestCommentsJson).length > 0"
                    class="comment-preview-box" @click.stop="toggleComments(session.chatSessionId)">
                    <div v-if="session.chatSessionCommentCnt > parseCommentPreviews(session.latestCommentsJson).length" class="comment-preview-more">
                      <van-icon name="arrow-down" size="12" color="#8A8A8A" />
                      <span>{{ $t('chatSessionComment_viewEarlier', {n: session.chatSessionCommentCnt - parseCommentPreviews(session.latestCommentsJson).length}) }}</span>
                    </div>
                    <div v-for="(preview, pIdx) in [...parseCommentPreviews(session.latestCommentsJson)].reverse()" :key="pIdx" class="comment-preview-item">
                      <img v-if="preview.userAvatar" :src="$common.getFullImageUrl(preview.userAvatar, $config, 's')" class="comment-preview-avatar" />
                      <div v-else class="comment-preview-avatar-default">{{ (preview.userName || 'A')[0] }}</div>
                      <div class="comment-preview-body">
                        <div class="comment-preview-name">{{ preview.userName || $t('anonymous') }}</div>
                        <div class="comment-preview-content"><span v-if="preview.replyToUserName" class="comment-preview-reply-to">@{{ preview.replyToUserName }} </span>{{ preview.content }}</div>
                      </div>
                    </div>
                  </div>
                  <!-- 评论展开区域（在气泡内部） -->
                  <div v-if="isMember && commentStates[session.chatSessionId] && commentStates[session.chatSessionId].expanded"
                    class="reply-section" @click.stop>
                    <div class="reply-list">
                      <div v-if="commentStates[session.chatSessionId].loading" style="text-align: center; padding: 8px;">
                        <van-loading size="16" />
                      </div>
                      <template v-else-if="commentStates[session.chatSessionId].comments.length === 0">
                        <div class="reply-empty">{{ $t('channel_chat_session_noComments') }}</div>
                      </template>
                      <template v-else>
                        <div v-for="comment in commentStates[session.chatSessionId].comments" :key="comment.chatSessionCommentId"
                          class="reply-item" @click="setReplyingTo(session.chatSessionId, comment)" @longpress="onCommentLongPress(session.chatSessionId, comment)">
                          <img v-if="comment.userAvatar" :src="$common.getFullImageUrl(comment.userAvatar, $config, 's')" class="reply-avatar" />
                          <div v-else class="reply-avatar-placeholder">{{ (comment.userName || 'A')[0] }}</div>
                          <div class="reply-body">
                            <span class="reply-user">{{ comment.userName || $t('anonymous') }}</span>
                            <span v-if="comment.replyToUserName" class="reply-to-user">@{{ comment.replyToUserName }}</span>
                            <span class="reply-colon">: </span>
                            <span class="reply-content">{{ comment.content }}</span>
                          </div>
                          <span v-if="!isCommentOwner(comment)" class="reply-to-btn" @click.stop="setReplyingTo(session.chatSessionId, comment)">{{ $t('chatSessionComment_replyTo') }}</span>
                        </div>
                        <div v-if="commentStates[session.chatSessionId].hasMore" class="reply-load-more"
                          @click="loadMoreComments(session.chatSessionId)">
                          {{ $t('chatSessionComment_loadMore') }}
                        </div>
                      </template>
                    </div>
                    <div class="reply-input-area">
                      <div class="reply-input-wrapper">
                        <textarea
                          v-model="commentStates[session.chatSessionId].inputText"
                          :placeholder="commentStates[session.chatSessionId].replyingTo ? ('@' + (commentStates[session.chatSessionId].replyingTo.userName || $t('anonymous')) + ' ' + $t('chatSessionComment_inputHint')) : $t('chatSessionComment_inputHint')"
                          class="reply-input"
                          rows="2"
                          @keyup.enter="sendComment(session.chatSessionId)"
                        ></textarea>
                        <van-icon v-if="commentStates[session.chatSessionId].replyingTo" name="close" size="14" color="#999" class="reply-clear-replying" @click="clearReplyingTo(session.chatSessionId)" />
                      </div>
                      <div class="reply-send-btn" :class="{ 'reply-send-btn-disabled': !commentStates[session.chatSessionId].inputText.trim() || commentStates[session.chatSessionId].sending }"
                        @click="sendComment(session.chatSessionId)">
                        <van-loading v-if="commentStates[session.chatSessionId].sending" size="14" color="white" />
                        <span v-else>{{ $t('chatSessionComment_send') || 'Send' }}</span>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
                <!-- 互动区域：点赞预览 + 操作按钮栏（胶囊样式，紧跟气泡下方） -->
                <div v-if="isMember && (isMySession(session) || session.public == 1) || (session.likeCnt > 0 && parseLikePreviews(session.recentLikesJson).length > 0)"
                  class="interaction-section" :class="{ 'interaction-section-mine': isMySession(session) }">
                  <!-- 最近点赞用户 -->
                  <div v-if="session.likeCnt > 0 && parseLikePreviews(session.recentLikesJson).length > 0" class="like-preview-bar">
                    <van-icon name="good-job" size="12" color="#E8543A" />
                    <span class="like-preview-names">
                      <span v-for="(liker, lIdx) in parseLikePreviews(session.recentLikesJson)" :key="lIdx">{{ liker.userName }}<span v-if="lIdx < parseLikePreviews(session.recentLikesJson).length - 1">, </span></span>
                      <span v-if="session.likeCnt > parseLikePreviews(session.recentLikesJson).length"> ...</span>
                    </span>
                  </div>
                  <!-- 互动按钮行（胶囊样式） -->
                  <div v-if="isMember && (isMySession(session) || session.public == 1)" class="interaction-actions">
                    <div class="interaction-action-btn interaction-action-btn-match" @click.stop="viewMatchHistory(session.chatSessionId)">
                      <span v-if="session.public == 1 && session.matchPaused != 1" class="match-live-icon">
                        <span class="match-live-ripple"></span>
                        <span class="match-live-ripple match-live-ripple2"></span>
                        <van-icon name="friends-o" size="13" color="#2A8FAD" class="match-live-core" />
                      </span>
                      <van-icon v-else-if="isMySession(session) && session.matchPaused == 1" name="pause-circle-o" size="13" color="#8A8A8A" />
                      <van-icon v-else name="friends-o" size="13" color="#2A8FAD" />
                      <span :style="isMySession(session) && session.matchPaused == 1 ? 'color:#8A8A8A' : ''">{{ $tc('chatSessionMatch_count', session.matchCnt, {n: session.matchCnt}) }}</span>
                    </div>
                    <div v-if="isMySession(session) || session.public == 1" class="interaction-action-btn" :class="{ 'active': commentStates[session.chatSessionId] && commentStates[session.chatSessionId].expanded }" @click.stop="toggleComments(session.chatSessionId)">
                      <van-icon name="comment-o" size="13" :color="commentStates[session.chatSessionId] && commentStates[session.chatSessionId].expanded ? '#E8543A' : '#8A8A8A'" />
                      <span>{{ getCommentCount(session) > 0 ? getCommentCount(session) : $t('chatSessionComment_reply') }}</span>
                      <van-icon v-if="getCommentCount(session) > 0 && !(commentStates[session.chatSessionId] && commentStates[session.chatSessionId].expanded)" name="arrow-down" size="11" color="#8A8A8A" />
                      <van-icon v-else-if="commentStates[session.chatSessionId] && commentStates[session.chatSessionId].expanded" name="arrow-up" size="11" color="#E8543A" />
                    </div>
                    <div class="interaction-action-btn" :class="{ 'active': session.liked }" @click.stop="toggleLike(session.chatSessionId)">
                      <van-icon :name="session.liked ? 'good-job' : 'good-job-o'" size="13" :color="session.liked ? '#E8543A' : '#8A8A8A'" />
                      <span>{{ session.likeCnt > 0 ? session.likeCnt : $t('chatSessionLike_like') }}</span>
                    </div>
                    <div class="interaction-action-btn" :class="{ 'active': session.bookmarked }" @click.stop="toggleBookmark(session.chatSessionId)">
                      <van-icon :name="session.bookmarked ? 'star' : 'star-o'" size="13" :color="session.bookmarked ? '#F5C842' : '#8A8A8A'" />
                      <span>{{ session.bookmarkCnt > 0 ? session.bookmarkCnt : $t('chatSessionBookmark_bookmark') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

      <div v-if="sessions.length === 0 && joinMsgs.length === 0 && !loading && hasLoaded" class="channel-empty-state">
        <div class="channel-empty-icon">
          <div class="channel-empty-icon-bg">
            <img class="channel-empty-icon-logo" src="/static/logo-transparent.png" alt="logo" />
          </div>
        </div>
        <div class="channel-empty-title">{{ $t('channelEmpty_title') }}</div>
        <div class="channel-empty-subtitle">{{ $t('channelEmpty_subtitle') }}</div>
        <div class="channel-empty-hint">
          <van-icon name="star-o" size="10" color="#E8543A" class="channel-empty-hint-icon" />
          <span>{{ $t('channelEmpty_hint') }}</span>
        </div>
        <div v-if="channel.memberCnt" class="channel-empty-members">
          <van-icon name="friends-o" size="14" color="#999" />
          <span>{{ channel.memberCnt }} {{ $t('channel_detail_members') }}</span>
        </div>
      </div>

      <!-- 顶部加载更多指示器（加载更早消息） -->
      <div v-if="loading && hasMore && sessions.length > 0" style="text-align: center; padding: 16px;">
        <van-loading size="20" />
      </div>
      <!-- 底部加载更新指示器（jumpToSession 后） -->
      <div v-if="loading && canLoadNewer && sessions.length > 0" style="text-align: center; padding: 16px;">
        <van-loading size="20" />
      </div>
      </div>
    </div>

    <!-- 底部操作栏（isMember 为 null 表示后端 membership 还没回来，两种底栏都不渲染，避免先进群按钮闪一下） -->
    <div v-if="isMember === false" class="channel-bottom-bar">
      <van-button type="primary" block color="#E8543A" @click="joinChannel" style="border-radius: 22px; height: 44px;">
        {{ $t('channel_detail_join') }}
      </van-button>
    </div>
    <!-- 成员时底部输入框：先输入 user msg，发送后打开 AI 聊天 -->
    <div v-if="isMember === true" class="channel-bottom-input-bar">
      <!-- 图片预览区：与 AI 弹框一致，显示在输入框上方；上传中展示 loading 遮罩 -->
      <div v-if="channelPendingImage || channelInputImage" class="channel-image-preview-container">
        <div class="channel-image-preview-item">
          <img v-if="channelPendingImage" :src="channelPendingImage" class="preview-image" />
          <img v-else :src="$common.getFullImageUrl(channelInputImage, $config)" class="preview-image" />
          <div v-if="uploadingImage" class="uploading-overlay">
            <van-loading size="16" />
          </div>
          <div class="image-delete-btn" @click="removeChannelInputImage">
            <van-icon name="cross" size="12" />
          </div>
        </div>
      </div>
      <div class="channel-input-wrapper">
        <!-- 加号按钮 -->
        <div class="channel-add-btn" @click.stop="toggleActionPanel">
          <van-icon name="plus" size="22" />
        </div>
        <input
          ref="channelFileInput"
          type="file"
          accept="image/*"
          style="display:none"
          @change="handleChannelImageSelect"
        />
        <input
          v-model="channelInputText"
          type="text"
          :placeholder="$t('channel_input_hint')"
          class="channel-input-field"
          @focus="onChannelInputFocus"
          @keyup.enter="onChannelInputSend"
        />
        <div
          class="channel-input-send"
          :class="{ 'channel-input-send-disabled': !channelInputText.trim() && !channelInputImage && !channelPendingImage }"
          @click="onChannelInputSend"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 12H19" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12.5 5.5L19 12L12.5 18.5" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>
      <!-- 动作面板 -->
      <div v-if="showActionPanel" class="channel-action-panel-overlay" @click.stop="showActionPanel = false"></div>
      <div v-if="showActionPanel" class="channel-action-panel">
        <div class="channel-action-panel-item" @click.stop="triggerChannelImageUpload">
          <div class="channel-action-panel-icon">
            <van-icon name="photo-o" size="28" />
          </div>
          <span class="channel-action-panel-label">{{ $t('action_photo') }}</span>
        </div>
      </div>
    </div>

    <!-- 设置弹窗 -->
  <van-popup v-model="showSettings" round position="bottom" :style="{ height: '55%' }">
      <div class="channel-settings">
        <div class="settings-header">
          <span style="font-weight: 600; font-size: 18px;">{{ channel.name || $t('channels_title') }}</span>
          <van-icon name="cross" size="20" color="#666" @click="showSettings = false" />
        </div>
        <!-- 分享群：唯一分享入口，点击弹出分享选项面板（链接优先，口令/二维码兜底） -->
        <div class="settings-item" @click="showChannelShareSheet = true">
          <div style="display: flex; align-items: center; gap: 8px;">
            <van-icon name="share-o" size="18" color="#E8543A" />
            <span>{{ $t('share_channel') }}</span>
          </div>
          <van-icon name="arrow" size="16" color="#ccc" />
        </div>
        <div v-if="channel" class="settings-item" @click="goToChannelMatchHistory">
          <div style="display: flex; align-items: center; gap: 8px;">
            <van-icon name="certificate" size="18" color="#E8543A" />
            <span>{{ $t('chatSessionMatch_matchHistory') }}</span>
          </div>
          <div style="display: flex; align-items: center; gap: 4px;">
            <span style="font-size: 14px; color: #8A8A8A;">{{ channel.totalMatchedCnt }}</span>
            <van-icon name="arrow" size="16" color="#ccc" />
          </div>
        </div>
        <div v-if="channel.channelId != '20210802' && channel.memberCnt" class="settings-item" @click="goToMembers">
          <div style="display: flex; align-items: center; gap: 8px;">
            <van-icon name="friends-o" size="18" color="#666" />
            <span>{{ $t('channel_detail_members') }}</span>
          </div>
          <div style="display: flex; align-items: center; gap: 4px;">
            <span style="font-size: 14px; color: #8A8A8A;">{{ channel.memberCnt }}</span>
            <van-icon name="arrow" size="16" color="#ccc" />
          </div>
        </div>
        <!-- 通知设置分组 -->
        <div v-if="channel.channelId != '20210802'" class="settings-notify-group">
          <div class="settings-notify-header">{{ $t('channel_settings_receiveNotifications') }}</div>
          <div class="settings-item settings-item-no-border">
            <span>{{ $t('channel_settings_push') }}</span>
            <van-switch v-model="pushEnabled" size="20" @change="updatePushSetting" />
          </div>
          <div class="settings-item settings-item-no-border">
            <span>{{ $t('channel_settings_commentPush') }}</span>
            <van-switch v-model="commentPushEnabled" size="20" @change="updateCommentPushSetting" />
          </div>
          <div class="settings-item settings-item-no-border">
            <span>{{ $t('channel_settings_likePush') }}</span>
            <van-switch v-model="likePushEnabled" size="20" @change="updateLikePushSetting" />
          </div>
        </div>
        <!-- 退群按钮（弱化处理） -->
        <div v-if="isMember && channel.channelId != '20210802'" class="settings-leave" @click="confirmLeave">
          {{ $t('channel_detail_leave') }}
        </div>
      </div>
    </van-popup>

    <!-- 分享群选项面板：分享链接（系统分享）/ 复制链接 / 二维码 / 群口令 -->
    <van-action-sheet
      v-model="showChannelShareSheet"
      :actions="channelShareSheetActions"
      :title="$t('share_channel')"
      cancel-text=" "
      close-on-click-action
    />

    <!-- 会话详情弹窗（评论+聊天） -->
    <van-popup v-model="showSessionDetail" round position="bottom" :style="{ height: '90%' }">
      <div class="session-detail-popup">
        <div class="session-detail-header">
          <!-- 头像 -->
          <div class="session-detail-avatar">
            <img v-if="currentSession.fromUserAvatar" :src="$common.getFullImageUrl(currentSession.fromUserAvatar, $config, 's')" class="session-detail-avatar-img" />
            <div v-else class="session-detail-avatar-placeholder">{{ (currentSession.fromUserName || '?')[0] }}</div>
          </div>
          <!-- 用户名 -->
          <div class="session-detail-user-info">
            <div class="session-detail-user-name">{{ currentSession.fromUserName || $t('anonymous') }}</div>
          </div>
          <!-- Find Matches 按钮（自己的session） -->
          <van-button v-if="isMySession(currentSession)" type="primary" size="mini" round
            style="background: #E8543A; border-color: #E8543A; margin-right: 4px;"
            @click="handleFindMatches">
            <div style="display: flex; align-items: center; gap: 4px;">
              <van-icon name="search" size="12" color="#fff" />
              <span style="font-size: 12px; color: #fff;">{{ $t('aiChat_findMatches') }}</span>
            </div>
          </van-button>
          <!-- 设置按钮（自己的session） -->
          <van-popover v-if="isMySession(currentSession)" v-model="showSessionSettings" trigger="click" placement="bottom-end" :actions="sessionSettingActions" @select="onSessionSettingSelect">
            <template #reference>
              <van-icon name="setting-o" size="20" color="#666" style="cursor: pointer; margin-right: 4px;" />
            </template>
          </van-popover>
          <!-- 私聊按钮（别人的session，对齐 client/ channel_detail_page：仅当卡片公开且作者允许直接联系才展示；含联系前广告闸门） -->
          <van-button v-if="!isMySession(currentSession) && currentSession.fromUserId && currentSession.allowDirectContact == 1 && currentSession.public == 1" type="primary" size="mini" round
            style="background: #E8543A; border-color: #E8543A; margin-right: 4px;"
            @click="onPrivateChat">
            <div style="display: flex; align-items: center; gap: 4px;">
              <van-icon name="chat-o" size="12" color="#fff" />
              <span style="font-size: 12px; color: #fff;">{{ $t('session_privateChat') }}</span>
            </div>
          </van-button>
          <!-- 分享按钮 -->
          <van-icon v-if="currentSession.chatSessionId && currentSession.channelId" name="share-o" size="20" color="#666" style="cursor: pointer; margin-right: 12px;" @click.stop="handleShareSession(currentSession)" />
          <!-- 关闭按钮 -->
          <van-icon name="cross" size="20" color="#666" @click="showSessionDetail = false" style="cursor: pointer;" />
        </div>
        <!-- User Msg + AI Summary 卡片（合并为一个容器，对齐 client） -->
        <div v-if="currentSession.userMsg || currentSession.aiSummary" class="session-detail-summary-card">
          <div class="session-detail-summary-inner">
            <div v-if="currentSession.userMsg" class="session-detail-usermsg-row">
              <span class="session-detail-usermsg-text">{{ $common.formatUserMsgPreview(currentSession.userMsg) }}</span>
            </div>
            <div v-if="currentSession.userMsg && currentSession.aiSummary" class="session-detail-summary-divider"></div>
            <div v-if="currentSession.aiSummary" class="session-detail-summary-row">
              <span style="display: inline-block; padding: 0 5px; background: #FEF0ED; color: #8A8A8A; font-size: 10px; border-radius: 4px; font-weight: 500; flex-shrink: 0; line-height: 1.4;">{{ $t('aiChat_matchSummaryLabel') }}</span>
              <span class="session-detail-ai-text" style="flex: 1;">{{ currentSession.aiSummary }}</span>
            </div>
          </div>
        </div>
        <!-- ChatBox -->
        <div v-if="currentSession.chatSessionId" class="session-detail-chat">
          <ChatBox v-if="showSessionDetail" ref="sessionDetailChatBox" :initialChatSessionId="String(currentSession.chatSessionId)"
            :channelId="channelId" :channelName="channel.name" :isEmbedded="true"
            :currentAvatarUrl="currentUser.avatar" :currentUserName="currentUser.userName"
            :showRequirementButtons="false" :showInputArea="false" :showUserMsgBanner="false"
            :chatMsgListStyle="{ minHeight: '0', maxHeight: 'none', backgroundColor: '#ECEAE6', padding: '12px' }" />
        </div>
      </div>
    </van-popup>

    <!-- 私聊前广告闸门（global，后端开关控制，与 MatchedSession 同一套） -->
    <ContactAdGate :visible="showContactAdGate" :seconds="contactGateSeconds" @continue="onContactGateContinue" />

    <!-- 私聊弹窗 -->
    <van-popup v-model="showPrivateChatPopup" round position="bottom" :style="{ height: '80%' }">
      <div class="contact-popup">
        <div class="contact-popup-header">
          <div class="contact-popup-title">{{ $t('chat_with', {name: privateChatName || $t('default_username')}) }}</div>
          <van-icon name="cross" size="20" color="#666" @click="showPrivateChatPopup = false" style="cursor: pointer;" />
        </div>
        <div class="contact-chat-box">
          <ChatBox v-if="showPrivateChatPopup"
            :targetUserId="privateChatUserId"
            :targetUserAvatar="privateChatAvatar"
            :targetUserName="privateChatName"
            :currentAvatarUrl="currentUser.avatar || currentUser.avatarUrl"
            :currentUserName="currentUser.userName || currentUser.email" />
        </div>
      </div>
    </van-popup>

    <!-- AI 聊天弹窗（发布需求） -->
    <van-popup v-model="showAiChat" round position="bottom" :style="{ height: '90%' }">
      <div class="session-detail-popup">
        <div class="session-detail-header">
          <van-icon name="cross" size="20" color="#4A4A4A" @click="showAiChat = false" />
          <span></span>
        </div>
        <div v-if="showAiChat" class="session-detail-chat">
          <ChatBox :channelId="channelId" :channelName="channel.name" :isEmbedded="true"
            :currentAvatarUrl="currentUser.avatar" :currentUserName="currentUser.userName"
            :initialMessage="aiChatInitialMessage"
            :initialContentType="aiChatInitialContentType"
            :initialMsgContent="aiChatInitialMsgContent"
            :skipRestore="aiChatSkipRestore"
            :showRequirementButtons="true"
            :chatMsgListStyle="{ minHeight: '0', maxHeight: 'none', backgroundColor: '#ECEAE6', padding: '12px' }"
            @published="onPublished"
            @ai-chat-reset="onAiChatReset" />
        </div>
      </div>
    </van-popup>

    <!-- 匹配历史弹窗（对齐 client/ MatchHistorySheet） -->
    <van-popup v-model="showMatchHistory" round position="bottom" :style="{ height: '85%' }">
      <div class="match-history-popup">
        <!-- Header -->
        <div class="match-history-header">
          <div style="display: flex; align-items: center; gap: 8px;">
            <van-icon name="link-o" size="20" color="#2A8FAD" />
            <span style="font-size: 16px; font-weight: 600; color: #1A1A1A;">{{ $t('chatSessionMatch_viewMatches') }}</span>
          </div>
          <van-icon name="cross" size="22" color="#8A8A8A" @click="showMatchHistory = false" style="cursor: pointer;" />
        </div>
        <!-- Content -->
        <div class="match-history-content" @scroll="onMatchHistoryScroll">
          <!-- 持续匹配横幅（暂停匹配时展示暂停态） -->
          <div class="match-history-banner" :class="{ 'match-history-banner-paused': isMyMatchSession && matchHistoryMatchPaused }">
            <van-icon v-if="isMyMatchSession && matchHistoryMatchPaused" name="pause-circle-o" size="22" color="#8A8A8A" class="match-history-paused-logo" />
            <img v-else src="/static/logo-transparent.png" class="match-history-spin-logo" />
            <div class="match-history-banner-text">
              <div class="match-history-banner-title" :style="isMyMatchSession && matchHistoryMatchPaused ? 'color:#4A4A4A' : ''">{{ isMyMatchSession ? (matchHistoryMatchPaused ? $t('match_matchingPausedTitle') : $t('match_continuousMatching')) : $t('match_continuousMatchingOthers') }}</div>
              <div class="match-history-banner-subtitle">
                {{ isMyMatchSession
                  ? (matchHistoryMatchPaused ? $t('match_matchingPausedHint') : (matchHistoryHasNew ? $t('match_newMatchFound', { n: matchHistoryNewCount }) : $t('match_continuousMatchingHint')))
                  : $t('match_continuousMatchingOthersHint') }}
              </div>
            </div>
            <div v-if="!(isMyMatchSession && matchHistoryMatchPaused)" class="match-history-pulse"></div>
          </div>
          <!-- 加载中 -->
          <div v-if="matchHistoryLoading && matchHistoryMatches.length === 0 && matchHistoryInterested.length === 0" style="padding: 60px 0; text-align: center;">
            <van-loading size="24" color="#E8543A" />
          </div>
          <!-- 匹配结果：matched 分组 -->
          <template v-if="!matchHistoryLoading && (matchHistoryMatches.length > 0 || matchHistoryInterested.length > 0)">
            <div v-if="matchHistoryMatches.length > 0" style="padding: 0 16px;">
              <div class="match-history-group-badge badge-matched">
                <van-icon name="like-o" size="14" color="#E8543A" />
                <span>{{ $t('label_perfect_match') }} ({{ matchHistoryMatches.length }})</span>
              </div>
              <div v-for="(item, index) in matchHistoryMatches" :key="'m-' + index"
                class="match-history-card" @click="goToMatchedSession(item)">
                <div class="match-history-card-badge badge-matched">{{ $t('label_perfect_match') }}</div>
                <div v-if="item.matchedUserMsg" class="match-history-card-msg">{{ $common.formatUserMsgPreview(item.matchedUserMsg) }}</div>
                <div v-if="item.matchedAiSummary" class="match-history-card-ai">
                  <span class="ai-summary-label">{{ $t('aiChat_matchSummaryLabel') }}</span>
                  <span>{{ item.matchedAiSummary }}</span>
                </div>
                <!-- 对方会话私有时展示提示（后端不下发内容） -->
                <div v-if="isPeerPrivate(item)" class="match-history-card-msg" style="display: flex; align-items: flex-start; gap: 4px;">
                  <van-icon name="lock" size="12" color="#8A8A8A" style="margin-top: 3px; flex-shrink: 0;" />
                  <span style="font-size: 12px; color: #8A8A8A; line-height: 1.4;">{{ $t('match_private_hint') }}</span>
                </div>
                <div class="match-history-card-meta">
                  <img v-if="item.matchedAvatar" :src="$common.getFullImageUrl(item.matchedAvatar, $config, 's')" class="match-history-card-avatar" />
                  <div v-else class="match-history-card-avatar-placeholder">{{ (item.matchedUserName || 'A')[0] }}</div>
                  <span class="match-history-card-name">{{ item.matchedUserName || $t('anonymous') }}</span>
                  <span v-if="item.createdAt" class="match-history-card-time">{{ $common.formatDateTime(item.createdAt) }}</span>
                </div>
                <div v-if="item.briefMatchReason" class="match-history-card-reason">
                  <span class="reason-tag">{{ $t('match_reasonTag') }}</span>
                  <span>{{ item.briefMatchReason }}</span>
                </div>
              </div>
            </div>
            <!-- interested 分组 -->
            <div v-if="matchHistoryInterested.length > 0" style="padding: 0 16px;">
              <div class="match-history-group-badge badge-interested">
                <van-icon name="star-o" size="14" color="#F5A623" />
                <span>{{ $t('label_worth_a_look') }} ({{ matchHistoryInterested.length }})</span>
              </div>
              <div v-for="(item, index) in matchHistoryInterested" :key="'i-' + index"
                class="match-history-card" @click="goToMatchedSession(item)">
                <div class="match-history-card-badge badge-interested">{{ $t('label_worth_a_look') }}</div>
                <div v-if="item.matchedUserMsg" class="match-history-card-msg">{{ $common.formatUserMsgPreview(item.matchedUserMsg) }}</div>
                <div v-if="item.matchedAiSummary" class="match-history-card-ai">
                  <span class="ai-summary-label">{{ $t('aiChat_matchSummaryLabel') }}</span>
                  <span>{{ item.matchedAiSummary }}</span>
                </div>
                <!-- 对方会话私有时展示提示（后端不下发内容） -->
                <div v-if="isPeerPrivate(item)" class="match-history-card-msg" style="display: flex; align-items: flex-start; gap: 4px;">
                  <van-icon name="lock" size="12" color="#8A8A8A" style="margin-top: 3px; flex-shrink: 0;" />
                  <span style="font-size: 12px; color: #8A8A8A; line-height: 1.4;">{{ $t('match_private_hint') }}</span>
                </div>
                <div class="match-history-card-meta">
                  <img v-if="item.matchedAvatar" :src="$common.getFullImageUrl(item.matchedAvatar, $config, 's')" class="match-history-card-avatar" />
                  <div v-else class="match-history-card-avatar-placeholder">{{ (item.matchedUserName || 'A')[0] }}</div>
                  <span class="match-history-card-name">{{ item.matchedUserName || $t('anonymous') }}</span>
                  <span v-if="item.createdAt" class="match-history-card-time">{{ $common.formatDateTime(item.createdAt) }}</span>
                </div>
                <div v-if="item.briefMatchReason" class="match-history-card-reason">
                  <span class="reason-tag">{{ $t('match_reasonTag') }}</span>
                  <span>{{ item.briefMatchReason }}</span>
                </div>
              </div>
            </div>
          </template>
          <!-- 无匹配结果 -->
          <div v-if="!matchHistoryLoading && matchHistoryMatches.length === 0 && matchHistoryInterested.length === 0" class="match-history-empty">
            <div class="match-history-empty-icon">
              <img src="/static/logo-transparent.png" class="match-history-empty-logo" />
            </div>
            <div class="match-history-empty-title">
              {{ isMyMatchSession ? $t('match_no_result_title') : $t('match_noResultsTitleOthers') }}
            </div>
            <div class="match-history-empty-hint">
              {{ isMyMatchSession ? $t('match_continuousMatchingHint') : $t('match_continuousMatchingOthersHint') }}
            </div>
          </div>
          <div v-if="matchHistoryHasMore" style="padding: 12px; text-align: center;">
            <van-button size="small" :loading="matchHistoryLoadingMore" @click="loadMoreMatchHistory">{{ $t('loadMore') || 'Load more' }}</van-button>
          </div>
        </div>
      </div>
    </van-popup>

    <!-- 二维码弹窗 -->
    <van-popup v-model="showQRPopup" round position="bottom" :style="{ height: '60%' }">
      <div class="qr-code-popup">
        <div class="qr-code-header">
          <span style="font-weight: 600;">{{ $t('channel_detail_qrCode') }}</span>
          <van-icon name="cross" size="20" color="#666" @click="showQRPopup = false" />
        </div>
        <div style="padding: 20px; text-align: center;">
          <!-- 合并后的截图：直接挂载真实 canvas 元素，避免 canvas.outerHTML 丢失像素变成空白 -->
          <div v-if="qrMergedCanvas" key="qrMergedCanvas" ref="qrMergedCanvasContainer" style="display: inline-block; cursor: pointer;" @click="downloadQRImage"></div>
          <!-- 用于 html2canvas 截图的渲染容器 -->
          <div v-else key="qrImageContainer" ref="qrImageContainer" style="display: inline-block; padding: 24px; border-radius: 16px; background: #fff; text-align: center; width: 248px;">
            <div style="display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 12px;">
              <img v-if="channel.avatar" :src="$common.getFullImageUrl(channel.avatar, $config)" style="width: 36px; height: 36px; border-radius: 16%; object-fit: cover; flex-shrink: 0;" />
              <div v-else style="width: 36px; height: 36px; border-radius: 16%; background: #E8543A; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 18px; font-weight: bold; flex-shrink: 0;">{{ (channel.name || '?')[0] }}</div>
              <span style="font-size: 16px; font-weight: bold; color: #1A1A1A; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ channel.name || '' }}</span>
            </div>
            <div v-if="channel.description" style="font-size: 13px; color: #8A8A8A; margin-bottom: 12px; line-height: 1.4; word-break: break-all; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">{{ channel.description }}</div>
            <vue-qr v-if="channel.channelCode" :text="getInviteUrl()" :size="200" :margin="0"></vue-qr>
          </div>
          <div style="margin-top: 8px;">
            <span style="font-size: 12px; color: #8A8A8A;">{{ $t('channels_joinByQRTitle') }}</span>
          </div>
          <div style="margin-top: 16px;">
            <span style="font-size: 12px; color: #8A8A8A;">{{ $t('channel_detail_qrCodeSaveHint') }}</span>
          </div>
        </div>
      </div>
    </van-popup>
    <!-- 需求卡快捷设置（卡片右上角齿轮直达：持续匹配/公开/允许直接联系） -->
    <van-action-sheet v-model="showQuickSettings" :actions="quickSettingActions"
      :cancel-text="$t('message_cancel')" close-on-click-action
      @select="onQuickSettingSelect" @cancel="showQuickSettings = false" />
  </div>
</template>

<script>
import axios from 'axios'
import Vue from 'vue'
import { Icon, Toast, Button, Empty, Loading, Popup, Switch, Dialog, Popover, ActionSheet } from 'vant'
import VueQr from 'vue-qr'
import html2canvas from 'html2canvas'
import ChatBox from './ChatBox.vue'
import AdBanner from './AdBanner.vue'
import ContactAdGate from './ContactAdGate.vue'
import contactAdGateMixin from '@/mixins/contactAdGate.js'
import wsService from '@/services/websocket.js'
import { shareChannel, shareSession, copyToClipboard, buildChannelShareUrl } from '@/services/share.js'

Vue.use(Icon)
Vue.use(Toast)
Vue.use(Button)
Vue.use(Empty)
Vue.use(Loading)
Vue.use(Popup)
Vue.use(Switch)
Vue.use(Dialog)
Vue.use(Popover)
Vue.use(ActionSheet)

export default {
  name: 'ChannelDetail',
  components: {
    ChatBox,
    AdBanner,
    ContactAdGate,
    VueQr
  },
  mixins: [contactAdGateMixin],
  data() {
    return {
      channelId: '',
      channel: {},
      // null = 后端 membership 未返回，底部栏两种形态都不渲染（防止进群按钮闪一下再消失）
      isMember: null,
      sessions: [],
      // 聚合进群消息（joinMsgs），单独存储，渲染时按 anchorPublishedAt 归并进 feedItems
      joinMsgs: [],
      loading: false,
      hasLoaded: false,
      hasMore: true,
      canLoadNewer: false,
      curPage: 1,
      pageSize: 20,
      showSettings: false,
      showChannelShareSheet: false,
      showQRPopup: false,
      qrMergedImageUrl: '',
      qrMergedCanvas: false,
      qrMerging: false,
      pushEnabled: true,
      commentPushEnabled: true,
      likePushEnabled: true,
      sessionsWrapperHeight: 'auto',
      showSessionDetail: false,
      showSessionSettings: false,
      currentSession: {},
      showAiChat: false,
      aiChatInitialMessage: '',
      aiChatInitialContentType: '',
      aiChatInitialMsgContent: '',
      // 本次打开弹框是否跳过草稿恢复（用户 reset 过本群 AI 会话后为 true）
      aiChatSkipRestore: false,
      showActionPanel: false,
      uploadingImage: false,
      // 重置过本群 AI 会话后，点击输入框仍弹 AI 对话，但弹空对话（skipRestore 不恢复草稿）
      aiChatResetted: false,
      channelInputText: '',
      channelInputImage: '',
      // 待上传/上传中图片的本地预览 URL（与 AI 弹框 pendingFiles 逻辑一致）
      channelPendingImage: '',
      // 评论状态
      commentStates: {},
      // 匹配历史
      showMatchHistory: false,
      matchHistoryLoading: false,
      matchHistoryList: [],
      matchHistoryMatches: [],
      matchHistoryInterested: [],
      matchHistoryChatSessionId: '',
      matchHistoryCurPage: 1,
      matchHistoryHasMore: false,
      matchHistoryLoadingMore: false,
      isMyMatchSession: false,
      matchHistoryMatchPaused: false,
      // 卡片右上角快捷设置
      showQuickSettings: false,
      quickSettingsSession: null,
      matchHistoryHasNew: false,
      matchHistoryNewCount: 0,
      matchHistoryKnownIds: [],
      // 匹配历史轮询
      matchHistoryTimer: null,
      // WebSocket
      wsChannelSessionHandler: null,
      wsChatSessionCommentHandler: null,
      wsChatSessionLikeHandler: null,
      // jumpToSession
      targetSessionId: null,
      highlightTarget: false,
      jumpingToSession: false,
      // 私聊弹窗（别人的session私聊，走 contactAdGate mixin 闸门）
      showPrivateChatPopup: false,
      privateChatUserId: '',
      privateChatName: '',
      privateChatAvatar: '',
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.user || {}
    },
    // feed 渲染列表：sessions（publishedAt DESC，index 0 = 最新 = 视觉底部）+ joinMsgs 归并
    // joinMsg 按其 anchorPublishedAt 插入到 anchor session 之后（数组更靠后/更旧的一侧）；
    // anchorPublishedAt 为 0（无 session 的群）的 joinMsg 排在所有 session 之后（数组末尾）
    feedItems() {
      const joins = this.joinMsgs || []
      if (joins.length === 0) return this.sessions
      const items = []
      for (const s of this.sessions) {
        items.push({ jm: null, s: s, t: s.publishedAt || s.createdAt || 0 })
      }
      for (const j of joins) {
        const anchor = Number(j.anchorPublishedAt || 0)
        items.push({ jm: j, s: null, t: anchor > 0 ? anchor : -1 })
      }
      items.sort((a, b) => {
        if (b.t !== a.t) return b.t - a.t
        // 时间相同：session 在前，joinMsg 紧随其后（更旧一侧）
        if ((a.jm ? 1 : 0) !== (b.jm ? 1 : 0)) return a.jm ? 1 : -1
        if (a.jm && b.jm) return Number(b.jm.lastJoinAt || 0) - Number(a.jm.lastJoinAt || 0)
        return 0
      })
      return items.map(it => it.jm ? { joinMsg: it.jm } : it.s)
    },
    // 分享群四选项：分享链接（系统分享）/ 复制链接 / 二维码 / 群口令
    channelShareSheetActions() {
      return [
        { name: this.$t('share_channel_link'), callback: () => { this.handleShareChannel() } },
        { name: this.$t('channel_detail_copyLink'), callback: () => { this.copyChannelLink() } },
        { name: this.$t('channel_detail_qrCode'), callback: () => { this.showQRCode() } },
        { name: this.$t('channel_detail_inviteCode'), callback: () => { this.copyChannelCode() } }
      ]
    },
    sessionSettingActions() {
      var actions = []
      // 持续匹配开关（仅公开需求卡；私密卡本来就不参与匹配）放第一位
      if (this.currentSession.public == 1) {
        actions.push({
          text: this.currentSession.matchPaused == 1 ? this.$t('session_resumeMatching') : this.$t('session_pauseMatching'),
          value: 'toggleMatchPaused'
        })
      }
      if (this.currentSession.public == 1) {
        actions.push({ text: this.$t('session_setPrivate'), value: 'setPrivate' })
      } else {
        actions.push({ text: this.$t('session_setPublic'), value: 'setPublic' })
      }
      var directLabel = this.$t('aiChat_allowDirectContact')
      if (this.currentSession.allowDirectContact == 1) {
        directLabel = directLabel + ' ✓'
      }
      actions.push({ text: directLabel, value: 'toggleDirectContact' })
      return actions
    },
    // 卡片右上角快捷设置动作项（与详情设置菜单同款能力）
    quickSettingActions() {
      var s = this.quickSettingsSession
      if (!s) return []
      var actions = []
      if (s.public == 1) {
        actions.push({
          name: s.matchPaused == 1 ? this.$t('session_resumeMatching') : this.$t('session_pauseMatching'),
          value: 'toggleMatchPaused'
        })
      }
      actions.push({
        name: s.public == 1 ? this.$t('session_setPrivate') : this.$t('session_setPublic'),
        value: s.public == 1 ? 'setPrivate' : 'setPublic'
      })
      var directLabel = this.$t('aiChat_allowDirectContact')
      if (s.allowDirectContact == 1) {
        directLabel = directLabel + ' ✓'
      }
      actions.push({ name: directLabel, value: 'toggleDirectContact' })
      return actions
    }
  },
  watch: {
    showAiChat(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // AI 聊天关闭后刷新会话列表
        this.refresh()
      }
    },
    showQRPopup(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // 二维码弹窗关闭后清理已生成的 canvas，避免重复打开时 DOM/内存堆积
        this.qrMergedCanvas = false
        this._qrCanvas = null
      }
    },
    showMatchHistory(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this._stopMatchHistoryTimer()
      }
    },
    '$route.params.id': {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.channelId = String(newVal)
          this.refresh().then(() => {
            // 检查是否需要定位到某个 session
            const chatSessionId = this.$route.query.chatSessionId
            if (chatSessionId) {
              this.jumpToSession(chatSessionId)
            }
          })
        }
      }
    }
  },
  mounted() {
    this.setupWebSocketListeners()
    this.calcSessionsHeight()
    window.addEventListener('resize', this.calcSessionsHeight)
  },
  beforeDestroy() {
    this.removeWebSocketListeners()
    this._stopMatchHistoryTimer()
    window.removeEventListener('resize', this.calcSessionsHeight)
  },
  methods: {
    // 对方会话是否私有（后端对私有会话不下发 matchedUserMsg/matchedAiSummary）
    isPeerPrivate (item) {
      return item.matchedPublic !== undefined && item.matchedPublic !== null && String(item.matchedPublic) !== '1'
    },
    // ===== WebSocket =====
    calcSessionsHeight() {
      this.$nextTick(() => {
        const pageEl = this.$el
        const headerEl = pageEl.querySelector('.channel-detail-header')
        const descEl = pageEl.querySelector('.channel-desc-banner')
        const sessionsHeaderEl = pageEl.querySelector('.channel-sessions-header')
        const bottomBarEl = pageEl.querySelector('.channel-bottom-bar, .channel-bottom-input-bar')
        const headerH = headerEl ? headerEl.offsetHeight : 0
        const descH = descEl ? descEl.offsetHeight : 0
        const sessionsHeaderH = sessionsHeaderEl ? sessionsHeaderEl.offsetHeight : 0
        const bottomBarH = bottomBarEl ? bottomBarEl.offsetHeight : 0
        const usedH = headerH + descH + sessionsHeaderH + bottomBarH
        const availH = window.innerHeight - pageEl.getBoundingClientRect().top
        this.sessionsWrapperHeight = Math.max(availH - usedH, 200) + 'px'
      })
    },
    setupWebSocketListeners() {
      this.wsChannelSessionHandler = (data) => {
        if (!data) return
        const channelId = String(data.channelId || '')
        const chatSessionId = String(data.chatSessionId || '')
        const action = data.action || 'publish'
        if (channelId !== this.channelId || !chatSessionId) return
        if (action === 'publish') {
          this.handleNewChannelChatSession(chatSessionId)
        }
      }
      this.wsChatSessionCommentHandler = (data) => {
        if (!data) return
        const channelId = String(data.channelId || '')
        const chatSessionId = String(data.chatSessionId || '')
        if (channelId !== this.channelId || !chatSessionId) return
        const cs = this.commentStates[chatSessionId]
        if (cs && cs.expanded) {
          this.refreshComments(chatSessionId)
        } else {
          this.refreshSessionCommentCount(chatSessionId)
        }
      }
      this.wsChatSessionLikeHandler = (data) => {
        if (!data) return
        const channelId = String(data.channelId || '')
        const chatSessionId = String(data.chatSessionId || '')
        if (channelId !== this.channelId || !chatSessionId) return
        // like通知仅用于提示，preview数据由后端latestCommentsJson/recentLikesJson提供
      }
      wsService.on('channel_chat_session', this.wsChannelSessionHandler, 'channel_detail_channel_session')
      wsService.on('chat_session_comment', this.wsChatSessionCommentHandler, 'channel_detail_chat_comment')
      wsService.on('chat_session_like', this.wsChatSessionLikeHandler, 'channel_detail_chat_like')
    },
    removeWebSocketListeners() {
      if (this.wsChannelSessionHandler) {
        wsService.off('channel_chat_session', 'channel_detail_channel_session')
      }
      if (this.wsChatSessionCommentHandler) {
        wsService.off('chat_session_comment', 'channel_detail_chat_comment')
      }
      if (this.wsChatSessionLikeHandler) {
        wsService.off('chat_session_like', 'channel_detail_chat_like')
      }
    },
    async jumpToSession(chatSessionId) {
      if (!chatSessionId) return
      // 先检查当前列表中是否已有该 session
      const found = this.sessions.find(s => s.chatSessionId === chatSessionId)
      if (found) {
        this.targetSessionId = chatSessionId
        this.highlightTarget = true
        this.$nextTick(() => {
          const el = this.$refs.sessionsContainer
          const targetEl = el.querySelector(`[data-session-id="${chatSessionId}"]`)
          if (targetEl) {
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
          // 2秒后取消高亮
          setTimeout(() => { this.highlightTarget = false }, 2000)
        })
        return
      }
      // 如果不在当前列表中，使用 get_session_page 获取页码
      this.jumpingToSession = true
      try {
        const pageResp = await axios.post('/api/channel/get_session_page', {
          channelId: this.channelId,
          chatSessionId: chatSessionId
        })
        let targetPage = 1
        if (pageResp.data.code === 0 && pageResp.data.data) {
          targetPage = pageResp.data.data.pageNum || 1
        }
        // 加载目标页及其前后页
        const pagesToLoad = [targetPage - 1, targetPage, targetPage + 1].filter(p => p >= 1)
        const allSessions = []
        const allJoinMsgs = []
        for (const page of pagesToLoad) {
          try {
            const resp = await axios.post('/api/channel/list_sessions', {
              channelId: this.channelId,
              pageSize: this.pageSize,
              curPage: page
            })
            if (resp.data.code === 0 && resp.data.data) {
              allSessions.push(...(resp.data.data.sessions || []))
              allJoinMsgs.push(...(resp.data.data.joinMsgs || []))
            }
          } catch (e) {
            console.error('[ChannelDetail] load page error:', e)
          }
        }
        // 去重并排序（DESC: index 0 = 最新 = 视觉底部）
        const seen = new Set()
        this.sessions = allSessions.filter(s => {
          if (seen.has(s.chatSessionId)) return false
          seen.add(s.chatSessionId)
          return true
        }).map(s => this.normalizeSession(s)).sort((a, b) => (b.publishedAt || b.createdAt || 0) - (a.publishedAt || a.createdAt || 0))
        // joinMsgs 去重后整组替换（排序由 feedItems 负责）
        const seenJoin = {}
        this.joinMsgs = allJoinMsgs.map(j => this.normalizeJoinMsg(j)).filter(j => {
          if (!j.id || seenJoin[j.id]) return false
          seenJoin[j.id] = true
          return true
        })
        // 检查是否找到目标
        const targetFound = this.sessions.some(s => s.chatSessionId === chatSessionId)
        if (targetFound) {
          this.targetSessionId = chatSessionId
          this.highlightTarget = true
          this.curPage = targetPage
          this.hasMore = targetPage > 1
          this.canLoadNewer = true
          this.$nextTick(() => {
            const el = this.$refs.sessionsContainer
            const targetEl = el.querySelector(`[data-session-id="${chatSessionId}"]`)
            if (targetEl) {
              targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
            setTimeout(() => { this.highlightTarget = false }, 2000)
          })
        } else {
          // 如果还是找不到，尝试直接获取 session 详情并插入
          const detailResp = await axios.post('/api/channel/get_session_detail', { chatSessionId })
          if (detailResp.data.code === 0 && detailResp.data.data && detailResp.data.data.session) {
            const session = this.normalizeSession(detailResp.data.data.session)
            if (!this.sessions.some(s => s.chatSessionId === chatSessionId)) {
              // column-reverse: unshift 到数组开头 = 视觉底部
              this.sessions.unshift(session)
            }
            this.targetSessionId = chatSessionId
            this.highlightTarget = true
            this.canLoadNewer = true
            this.$nextTick(() => {
              const el = this.$refs.sessionsContainer
              const targetEl = el.querySelector(`[data-session-id="${chatSessionId}"]`)
              if (targetEl) {
                targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
              }
              setTimeout(() => { this.highlightTarget = false }, 2000)
            })
          } else {
            Toast(this.$t('channel_detail_locateFailed'))
          }
        }
      } catch (error) {
        console.error('[ChannelDetail] jumpToSession error:', error)
        Toast(this.$t('channel_detail_locateError'))
      } finally {
        this.jumpingToSession = false
      }
    },
    async handleNewChannelChatSession(chatSessionId) {
      try {
        const response = await axios.post('/api/channel/get_session_detail', { chatSessionId })
        if (response.data.code === 0 && response.data.data && response.data.data.session) {
          const session = this.normalizeSession(response.data.data.session)
          // 避免重复添加
          if (this.sessions.some(s => s.chatSessionId === chatSessionId)) return
          // 新消息插入到数组开头 = 视觉底部（column-reverse 容器）
          this.sessions.unshift(session)
          this.$nextTick(() => {
            const el = this.$refs.sessionsContainer
            // column-reverse 容器：视觉底部 = scrollTop = 0
            if (el) el.scrollTop = 0
          })
        }
      } catch (e) {
        console.error('[ChannelDetail] handleNewChannelChatSession error:', e)
      }
    },

    // ===== Refresh / Load =====
    async refresh() {
      this.loading = true
      this.curPage = 1
      this.sessions = []
      this.joinMsgs = []
      this.hasMore = true
      this.canLoadNewer = false
      this.targetSessionId = null
      this.highlightTarget = false
      this.commentStates = {}
      await this.loadChannel()
      this.loading = false
      await this.loadSessions()
    },
    async loadChannel() {
      // 先置为未加载，等本群 /channel/get 回来再决定底部栏形态（避免切换群时闪出上一次的底栏）
      this.isMember = null
      try {
        const response = await axios.post('/api/channel/get', { channelId: this.channelId })
        if (response.data.code === 0 && response.data.data) {
          const data = response.data.data
          const ch = data.channel || {}
          this.channel = {
            channelId: String(ch.channelId || ''),
            name: ch.name || '',
            description: ch.description || '',
            avatar: ch.avatar || '',
            memberCnt: ch.memberCnt || 0,
            chatSessionCnt: ch.chatSessionCnt || 0,
            channelCode: ch.channelCode || '',
            creator: String(ch.creator || ''),
            createdAt: ch.createdAt || 0,
            totalMatchedCnt: ch.totalMatchedCnt || 0,
          }
          this.isMember = data.isMember || data.joined || false
          this.pushEnabled = data.pushEnabled !== false
          this.commentPushEnabled = data.commentPushEnabled !== false
          this.likePushEnabled = data.likePushEnabled !== false
          this.calcSessionsHeight()
        }
      } catch (error) {
        console.error('[ChannelDetail] loadChannel error:', error)
      }
    },
    async loadSessions() {
      if (this.loading) return
      this.loading = true
      try {
        const response = await axios.post('/api/channel/list_sessions', {
          channelId: this.channelId,
          pageSize: this.pageSize,
          curPage: this.curPage
        })
        if (response.data.code === 0 && response.data.data) {
          const sessions = (response.data.data.sessions || []).map(s => this.normalizeSession(s))
          const pageJoinMsgs = response.data.data.joinMsgs || []
          if (this.curPage === 1) {
            // 后端返回 published_at DESC，index 0 = 最新 = 视觉底部
            // column-reverse 容器：直接使用，无需反转
            this.sessions = sessions
            this.joinMsgs = pageJoinMsgs.map(j => this.normalizeJoinMsg(j))
          } else {
            // 加载更早的 session，追加到数组末尾 = 视觉顶部
            // column-reverse 容器：滚动位置自然不变，无需 scrollHeight/scrollTop 修正！
            const existingIds = new Set(this.sessions.map(s => s.chatSessionId))
            const newSessions = sessions.filter(s => !existingIds.has(s.chatSessionId)).map(s => this.normalizeSession(s))
            this.sessions = this.sessions.concat(newSessions)
            this.mergeJoinMsgs(pageJoinMsgs)
          }
          this.hasMore = sessions.length >= this.pageSize
        } else {
          if (this.curPage === 1) {
            this.sessions = []
            this.joinMsgs = []
          }
          this.hasMore = false
        }
      } catch (error) {
        console.error('[ChannelDetail] loadSessions error:', error)
      } finally {
        this.loading = false
        this.hasLoaded = true
        // 首次加载后滚动到底部
        if (this.curPage === 1) {
          this.$nextTick(() => {
            const el = this.$refs.sessionsContainer
            // column-reverse 容器：视觉底部 = scrollTop = 0
            if (el) el.scrollTop = 0
          })
        }
      }
    },
    loadMore() {
      if (this.hasMore && !this.loading) {
        this.curPage++
        this.loadSessions()
      }
    },
    onSessionsScroll() {
      const el = this.$refs.sessionsContainer
      if (!el || this.loading || this.jumpingToSession) return
      // column-reverse 容器中 scrollTop 的行为：
      //   scrollTop = 0 → 视觉底部（最新 session）
      //   scrollTop = -(scrollHeight - clientHeight) → 视觉顶部（更早 session）
      // 滚动到视觉顶部（更早 session）时，加载更早的 session
      const minScroll = -(el.scrollHeight - el.clientHeight)
      if (el.scrollTop <= minScroll + 100 && this.hasMore) {
        this.loadMore()
      }
      // 滚动到视觉底部（最新 session）时，加载更新的 session（仅 jumpToSession 后）
      if (el.scrollTop >= -200 && this.canLoadNewer) {
        this.loadNewerSessions()
      }
    },
    async loadNewerSessions() {
      if (this.loading || !this.canLoadNewer || this.sessions.length === 0) return
      const latestSession = this.sessions[this.sessions.length - 1]
      const afterTime = latestSession.publishedAt || latestSession.createdAt || 0
      if (!afterTime) return
      this.loading = true
      try {
        const response = await axios.post('/api/channel/list_sessions', {
          channelId: this.channelId,
          pageSize: this.pageSize,
          afterTime: afterTime
        })
        if (response.data.code === 0 && response.data.data) {
          const sessions = (response.data.data.sessions || []).map(s => this.normalizeSession(s))
          // joinMsgs 一并归并（按 id 去重，排序由 feedItems 负责）
          this.mergeJoinMsgs(response.data.data.joinMsgs || [])
          if (sessions.length === 0) {
            this.canLoadNewer = false
          } else {
            const existingIds = new Set(this.sessions.map(s => s.chatSessionId))
            const newSessions = sessions.filter(s => !existingIds.has(s.chatSessionId))
            // 新消息插入到数组开头 = 视觉底部（column-reverse 容器）
            this.sessions = newSessions.concat(this.sessions)
            this.canLoadNewer = sessions.length >= this.pageSize
          }
        } else {
          this.canLoadNewer = false
        }
      } catch (error) {
        console.error('[ChannelDetail] loadNewerSessions error:', error)
      } finally {
        this.loading = false
      }
    },

    // ===== Join / Leave =====
    async confirmLeave() {
      try {
        await Dialog.confirm({
          title: this.$t('channel_detail_leave'),
          message: this.$t('channel_leave_confirm_message'),
          confirmButtonText: this.$t('channel_detail_leave'),
          confirmButtonColor: '#E8543A',
          cancelButtonText: this.$t('cancel')
        })
        this.showSettings = false
        await this.leaveChannel()
      } catch (e) {
        // 用户取消
      }
    },
    async joinChannel() {
      // 匿名用户（没有 email 且没有 mobile 的临时账号）需要先登录才能加群（对齐 client/ channel_detail_page._join）
      if (!this.$store.state.alreadyLogin) {
        this.$store.state.needLogin = true
        return
      }
      const user = this.$store.state.user || {}
      if ((!user.email || !user.email.trim()) && (!user.mobile || !String(user.mobile).trim())) {
        this.$store.state.needLogin = true
        return
      }
      try {
        const response = await axios.post('/api/channel/join', { channelId: this.channelId })
        if (response.data.code === 0) {
          Toast.success(this.$t('operationSuccess'))
          this.isMember = true
          this.loadChannel()
        } else {
          Toast.fail(response.data.data || this.$t('operationFailed'))
        }
      } catch (error) {
        console.error('[ChannelDetail] joinChannel error:', error)
        Toast.fail(this.$t('operationFailed'))
      }
    },
    async leaveChannel() {
      try {
        const response = await axios.post('/api/channel/leave', { channelId: this.channelId })
        if (response.data.code === 0) {
          Toast.success(this.$t('operationSuccess'))
          this.isMember = false
          this.loadChannel()
        } else {
          Toast.fail(response.data.data || this.$t('operationFailed'))
        }
      } catch (error) {
        console.error('[ChannelDetail] leaveChannel error:', error)
        Toast.fail(this.$t('operationFailed'))
      }
    },

    // ===== Settings =====
    async updatePushSetting(val) {
      try {
        await axios.post('/api/channel/update_push_setting', {
          channelId: this.channelId,
          pushEnabled: val
        })
      } catch (error) {
        console.error('[ChannelDetail] updatePushSetting error:', error)
      }
    },
    async updateCommentPushSetting(val) {
      try {
        await axios.post('/api/channel/update_comment_push_setting', {
          channelId: this.channelId,
          commentPushEnabled: val
        })
      } catch (error) {
        console.error('[ChannelDetail] updateCommentPushSetting error:', error)
      }
    },
    async updateLikePushSetting(val) {
      try {
        await axios.post('/api/channel/update_like_push_setting', {
          channelId: this.channelId,
          likePushEnabled: val
        })
      } catch (error) {
        console.error('[ChannelDetail] updateLikePushSetting error:', error)
      }
    },
    goToChannelMatchHistory() {
      this.$router.push({ path: '/channel-match-history/' + this.channelId })
    },
    goToMembers() {
      this.showSettings = false
      this.$router.push({ path: '/channel-members/' + this.channelId })
    },
    goToSearch() {
      this.showSettings = false
      this.$router.push({ path: '/channel-search/' + this.channelId })
    },
    // 打开 AI 聊天（可选初始消息和内容类型；skipRestore=true 时不恢复未发布草稿）
    openAiChat(initialMessage = '', initialContentType = '', initialMsgContent = '', skipRestore = false) {
      // 统一收起输入框焦点/软键盘（回车发送路径不会原生 blur，弹层开着时键盘会悬着）
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur()
      }
      this.aiChatSkipRestore = skipRestore
      this.aiChatInitialMessage = initialMessage
      this.aiChatInitialContentType = initialContentType
      this.aiChatInitialMsgContent = initialMsgContent
      this.showAiChat = true
    },
    // 点击群聊下方输入框：总是拉起 AI 对话（发送路径本来就是无条件进 AI 对话，这里统一语义）；
    // reset 过本群 AI 会话后弹空对话、不恢复草稿
    onChannelInputFocus() {
      this.openAiChat('', '', '', this.aiChatResetted)
    },
    onAiChatReset() {
      // 重置只清空会话（ChatBox 内部已处理），弹框保持打开，避免再次拉起时又拉取历史
      // aiChatResetted 标记用于关闭后再点输入框时跳过草稿恢复
      this.aiChatResetted = true
    },
    onChannelInputSend() {
      const text = this.channelInputText.trim()
      const image = this.channelInputImage
      if (!text && !image) return
      if (!this.$store.state.alreadyLogin) {
        this.$store.state.needLogin = true
        return
      }
      this.channelInputText = ''
      // 用户已真正输入发送，重置标记清除，后续点击输入框可再次自动打开 AI 对话
      this.aiChatResetted = false
      if (image) {
        this.channelInputImage = ''
        this.openAiChat(text || '', 'image_url', image)
      } else {
        this.openAiChat(text)
      }
    },
    toggleActionPanel() {
      this.showActionPanel = !this.showActionPanel
    },
    triggerChannelImageUpload() {
      this.showActionPanel = false
      if (this.$refs.channelFileInput) {
        this.$refs.channelFileInput.value = ''
        this.$refs.channelFileInput.click()
      }
    },
    async handleChannelImageSelect(e) {
      var file = e.target.files && e.target.files[0]
      if (!file) return
      if (this.uploadingImage) return

      // 检查是否为图片
      if (!file.type || !file.type.startsWith('image/')) {
        Toast(this.$t('channel_select_image'))
        return
      }

      if (!this.$store.state.alreadyLogin) {
        this.$store.state.needLogin = true
        return
      }

      // 与 AI 弹框一致：先展示本地预览（输入框上方），上传中显示 loading 遮罩
      this.uploadingImage = true
      this.channelPendingImage = URL.createObjectURL(file)
      try {
        var compressed = await this.compressImage(file)
        var url = await this.uploadFileToS3(compressed)
        this.channelInputImage = url
        // 上传完成后替换为正式图并收起 loading
        if (this.channelPendingImage) {
          URL.revokeObjectURL(this.channelPendingImage)
        }
        this.channelPendingImage = ''
      } catch (err) {
        console.error('[ChannelDetail] upload image error:', err)
        if (this.channelPendingImage) {
          URL.revokeObjectURL(this.channelPendingImage)
        }
        this.channelPendingImage = ''
        this.channelInputImage = ''
        Toast(this.$t('channel_image_upload_failed'))
      } finally {
        this.uploadingImage = false
      }
    },
    // 移除输入框上方的图片预览（与 AI 弹框 removeFile 行为一致）
    removeChannelInputImage() {
      if (this.channelPendingImage) {
        URL.revokeObjectURL(this.channelPendingImage)
      }
      this.channelPendingImage = ''
      this.channelInputImage = ''
      this.uploadingImage = false
    },
    compressImage(file) {
      return new Promise(function(resolve, reject) {
        var reader = new FileReader()
        reader.onload = function(e) {
          var img = new window.Image()
          img.onload = function() {
            var canvas = document.createElement('canvas')
            var maxSize = 1024
            var width = img.width
            var height = img.height
            if (width > maxSize || height > maxSize) {
              if (width > height) {
                height = Math.round((height * maxSize) / width)
                width = maxSize
              } else {
                width = Math.round((width * maxSize) / height)
                height = maxSize
              }
            }
            canvas.width = width
            canvas.height = height
            canvas.getContext('2d').drawImage(img, 0, 0, width, height)
            canvas.toBlob(
              function(blob) {
                if (blob) {
                  resolve(new File([blob], file.name, { type: 'image/jpeg' }))
                } else {
                  reject(new Error('Canvas toBlob failed'))
                }
              },
              'image/jpeg',
              0.8
            )
          }
          img.onerror = reject
          img.src = e.target.result
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    async uploadFileToS3(file) {
      try {
        // 生成唯一文件名
        var fileExtension = file.name.split('.').pop()
        var fileName = (this.currentUser && this.currentUser.userId || 'user') + '_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9) + '.' + fileExtension
        var mimeType = file.type || 'image/jpeg'

        // 调用后端获取预签名 URL
        var presignResponse = await axios.post('/api/chat/get_s3_presigned_url', {
          fileName: fileName,
          fileType: mimeType,
          fileSize: file.size
        })
        if (presignResponse.data.code != 0) {
          return Promise.reject(presignResponse.data.data || 'upload failed')
        }

        var uploadUrl = presignResponse.data.data.uploadUrl
        var imagePath = presignResponse.data.data.imagePath

        // 上传到 S3
        await axios.put(uploadUrl, file, {
          headers: { 'Content-Type': mimeType }
        })

        return imagePath
      } catch (error) {
        console.error('[uploadFileToS3] error:', error)
        return Promise.reject(error)
      }
    },
    onPublished(chatSessionId) {
      // 发布成功后关闭 AI 聊天弹窗，并刷新会话列表
      this.showAiChat = false
      this.refresh()
      // 自动弹出匹配结果页，告知用户小摇正在匹配
      if (chatSessionId) {
        var self = this
        setTimeout(function() {
          self.viewMatchHistory(chatSessionId)
        }, 500)
      }
    },

    // ===== Session Detail =====
    openSessionDetail(session) {
      // 别人的私有 session 不可点击
      if (!this.isMySession(session) && session.public != 1) return
      this.currentSession = session
      this.showSessionDetail = true
    },
    isMySession(session) {
      const myUserId = String(this.currentUser.userId || '')
      return String(session.fromUserId || '') === myUserId
    },
    // 私聊别人的 session（对齐 client/ channel_detail_page._onPrivateChat：登录检查 + 联系前广告闸门）
    onPrivateChat() {
      if (!this.$store.state.alreadyLogin) {
        this.$store.state.needLogin = true
        return
      }
      const user = this.$store.state.user || {}
      if ((!user.email || !user.email.trim()) && (!user.mobile || !String(user.mobile).trim())) {
        this.$store.state.needLogin = true
        return
      }
      if (this.tryContactGate()) return
      this.openPrivateChatPopup()
    },
    // 闸门放行后打开私聊弹窗（mixin 钩子）
    gateContinue() {
      this.openPrivateChatPopup()
    },
    openPrivateChatPopup() {
      const session = this.currentSession || {}
      const targetUserId = String(session.fromUserId || '')
      if (!targetUserId) return
      this.privateChatUserId = targetUserId
      this.privateChatName = session.fromUserName || ''
      this.privateChatAvatar = session.fromUserAvatar || ''
      this.showPrivateChatPopup = true
    },
    handleFindMatches() {
      // 直接使用 ChannelDetail 的匹配结果 Sheet，与发布后的弹窗保持一致
      if (this.currentSession && this.currentSession.chatSessionId) {
        this.viewMatchHistory(this.currentSession.chatSessionId)
      }
    },
    async onSessionSettingSelect(action) {
      this.showSessionSettings = false
      if (action.value === 'setPublic') {
        this.confirmSetPublic(this.currentSession)
      } else if (action.value === 'setPrivate') {
        this.confirmSetPrivate(this.currentSession)
      } else if (action.value === 'toggleDirectContact') {
        this.toggleDirectContact(this.currentSession)
      } else if (action.value === 'toggleMatchPaused') {
        this.toggleMatchPaused(this.currentSession)
      }
    },
    // 卡片右上角快捷设置
    openQuickSettings(session) {
      this.quickSettingsSession = session
      this.showQuickSettings = true
    },
    onQuickSettingSelect(action) {
      var s = this.quickSettingsSession
      this.showQuickSettings = false
      if (!s) return
      if (action.value === 'setPublic') {
        this.confirmSetPublic(s)
      } else if (action.value === 'setPrivate') {
        this.confirmSetPrivate(s)
      } else if (action.value === 'toggleDirectContact') {
        this.toggleDirectContact(s)
      } else if (action.value === 'toggleMatchPaused') {
        this.toggleMatchPaused(s)
      }
    },
    confirmSetPublic(session) {
      this.$dialog.confirm({
        title: this.$t('aiChat_setPublicTitle'),
        message: this.$t('aiChat_setPublicMessage'),
      }).then(() => {
        this.toggleSessionPublic(session, 1)
      }).catch(() => {})
    },
    confirmSetPrivate(session) {
      this.$dialog.confirm({
        message: this.$t('session_confirmSetPrivate'),
      }).then(() => {
        this.toggleSessionPublic(session, 0)
      }).catch(() => {})
    },
    async toggleMatchPaused(session) {
      try {
        var newMatchPaused = session.matchPaused == 1 ? 0 : 1
        const response = await axios.post('/api/chat/update_match_paused', {
          chatSessionId: session.chatSessionId,
          matchPaused: newMatchPaused
        })
        if (response.data.code === 0) {
          this.$set(session, 'matchPaused', newMatchPaused)
          Toast.success(newMatchPaused == 1 ? this.$t('session_matchPausedToast') : this.$t('session_matchResumedToast'))
          this.refresh()
        } else {
          Toast.fail(response.data.data || 'Error')
        }
      } catch (e) {
        console.error('[ChannelDetail] toggleMatchPaused error:', e)
      }
    },
    async toggleSessionPublic(session, publicVal) {
      try {
        var req = { chatSessionId: session.chatSessionId }
        if (publicVal == 1) {
          req.public = 1
        } else {
          req.public = -1
        }
        const response = await axios.post('/api/chat/update_public', req)
        if (response.data.code === 0) {
          this.$set(session, 'public', publicVal)
          Toast.success(publicVal == 1 ? this.$t('session_setPublic') : this.$t('session_setPrivate'))
          this.refresh()
        } else {
          Toast.fail(response.data.data || 'Error')
        }
      } catch (e) {
        console.error('[ChannelDetail] toggleSessionPublic error:', e)
      }
    },
    async toggleDirectContact(session) {
      try {
        var newVal = session.allowDirectContact == 1 ? 0 : 1
        await axios.post('/api/channel/update_direct_contact', {
          chatSessionId: session.chatSessionId,
          allowDirectContact: newVal
        })
        this.$set(session, 'allowDirectContact', newVal)
        Toast.success(this.$t('session_setDirectContactSuccess'))
      } catch (e) {
        console.error('[ChannelDetail] toggleDirectContact error:', e)
      }
    },

    // ===== Comments =====
    getCommentCount(session) {
      const chatSessionId = session.chatSessionId
      const cs = this.commentStates[chatSessionId]
      return cs ? (cs.totalCount || 0) : (session.chatSessionCommentCnt || 0)
    },
    async toggleComments(chatSessionId) {
      if (!this.$store.state.alreadyLogin || !this.$store.state.user.userName) {
        this.$store.state.needLogin = true
        return
      }
      if (!this.commentStates[chatSessionId]) {
        Vue.set(this.commentStates, chatSessionId, {
          expanded: false,
          comments: [],
          inputText: '',
          sending: false,
          loading: false,
          hasMore: false,
          curPage: 1,
          totalCount: 0,
          replyingTo: null
        })
      }
      const cs = this.commentStates[chatSessionId]
      cs.expanded = !cs.expanded
      if (cs.expanded && cs.comments.length === 0) {
        await this.loadComments(chatSessionId, 1)
      }
    },
    async loadComments(chatSessionId, page) {
      const cs = this.commentStates[chatSessionId]
      if (!cs || cs.loading) return
      cs.loading = true
      try {
        const response = await axios.post('/api/chat_session_comment/list', {
          chatSessionId: chatSessionId,
          pageSize: 50,
          curPage: page
        })
        if (response.data.code === 0 && response.data.data) {
          const comments = response.data.data.chatSessionComments || []
          if (page === 1) {
            cs.comments = comments
          } else {
            cs.comments = cs.comments.concat(comments)
          }
          cs.hasMore = comments.length >= 50
          cs.totalCount = response.data.data.total || cs.comments.length
        }
      } catch (error) {
        console.error('[ChannelDetail] loadComments error:', error)
      } finally {
        cs.loading = false
      }
    },
    loadMoreComments(chatSessionId) {
      const cs = this.commentStates[chatSessionId]
      if (!cs || !cs.hasMore) return
      cs.curPage++
      this.loadComments(chatSessionId, cs.curPage)
    },
    async refreshComments(chatSessionId) {
      const cs = this.commentStates[chatSessionId]
      if (!cs) return
      cs.curPage = 1
      await this.loadComments(chatSessionId, 1)
    },
    async refreshSessionCommentCount(chatSessionId) {
      // 简单刷新该 session 的评论数
      const cs = this.commentStates[chatSessionId]
      if (!cs) {
        // 初始化一个只记录总数的 state
        Vue.set(this.commentStates, chatSessionId, {
          expanded: false,
          comments: [],
          inputText: '',
          sending: false,
          loading: false,
          hasMore: false,
          curPage: 1,
          totalCount: 0,
          replyingTo: null
        })
      }
      try {
        const response = await axios.post('/api/chat_session_comment/list', {
          chatSessionId: chatSessionId,
          pageSize: 1,
          curPage: 1
        })
        if (response.data.code === 0 && response.data.data) {
          const total = response.data.data.total || 0
          if (this.commentStates[chatSessionId]) {
            this.commentStates[chatSessionId].totalCount = total
          }
        }
      } catch (e) {
        console.error('[ChannelDetail] refreshSessionCommentCount error:', e)
      }
    },
    async sendComment(chatSessionId) {
      if (!this.$store.state.alreadyLogin || !this.$store.state.user.userName) {
        this.$store.state.needLogin = true
        return
      }
      const cs = this.commentStates[chatSessionId]
      if (!cs || !cs.inputText.trim() || cs.sending) return
      const content = cs.inputText.trim()
      cs.sending = true
      try {
        const req = {
          chatSessionId: chatSessionId,
          content: content
        }
        if (cs.replyingTo) {
          req.replyToUserId = cs.replyingTo.userId || ''
          req.replyToUserName = cs.replyingTo.userName || 'Anonymous'
          req.replyToCommentId = cs.replyingTo.chatSessionCommentId || ''
        }
        const response = await axios.post('/api/chat_session_comment/create', req)
        if (response.data.code === 0 && response.data.data) {
          cs.inputText = ''
          cs.replyingTo = null
          // 本地更新 latestCommentsJson
          var myName = this.$store.state.user.userName || ''
          var myAvatar = this.$store.state.user.avatar || ''
          var previewContent = content
          if (previewContent.length > 50) {
            previewContent = previewContent.substring(0, 50) + '...'
          }
          var session = this.sessions.find(s => String(s.chatSessionId) === chatSessionId)
          if (session) {
            var newPreviews = [{ userName: myName, userAvatar: myAvatar, content: previewContent }]
            var oldPreviews = this.parseCommentPreviews(session.latestCommentsJson)
            if (oldPreviews.length > 0) {
              newPreviews.push(oldPreviews[0])
            }
            this.$set(session, 'latestCommentsJson', JSON.stringify(newPreviews))
            this.$set(session, 'chatSessionCommentCnt', (session.chatSessionCommentCnt || 0) + 1)
          }
          // 刷新评论列表
          await this.refreshComments(chatSessionId)
        } else {
          Toast.fail(response.data.data || this.$t('operationFailed'))
        }
      } catch (error) {
        console.error('[ChannelDetail] sendComment error:', error)
        Toast.fail(this.$t('operationFailed'))
      } finally {
        cs.sending = false
      }
    },
    async deleteComment(chatSessionCommentId) {
      try {
        const response = await axios.post('/api/chat_session_comment/delete', {
          chatSessionCommentId: chatSessionCommentId
        })
        if (response.data.code === 0) {
          Toast.success(this.$t('operationSuccess'))
          // 找到对应的 chatSessionId 并刷新
          for (const chatSessionId in this.commentStates) {
            const cs = this.commentStates[chatSessionId]
            if (cs.comments.some(c => c.chatSessionCommentId === chatSessionCommentId)) {
              await this.refreshComments(chatSessionId)
              // 删除后从评论列表中重新计算 latestCommentsJson
              var session = this.sessions.find(s => String(s.chatSessionId) === chatSessionId)
              if (session) {
                if (cs.comments.length > 0) {
                  // 重建 previews（最多2条）
                  var newPreviews = []
                  var start = Math.max(0, cs.comments.length - 2)
                  for (var i = cs.comments.length - 1; i >= start; i--) {
                    var c = cs.comments[i]
                    var pc = c.content || ''
                    if (pc.length > 50) pc = pc.substring(0, 50) + '...'
                    newPreviews.push({
                      userName: c.userName || '',
                      userAvatar: c.userAvatar || '',
                      content: pc
                    })
                  }
                  this.$set(session, 'latestCommentsJson', JSON.stringify(newPreviews))
                } else {
                  this.$set(session, 'latestCommentsJson', '')
                }
                this.$set(session, 'chatSessionCommentCnt', Math.max(0, (session.chatSessionCommentCnt || 0) - 1))
              }
              break
            }
          }
        } else {
          Toast.fail(response.data.data || this.$t('operationFailed'))
        }
      } catch (error) {
        console.error('[ChannelDetail] deleteComment error:', error)
        Toast.fail(this.$t('operationFailed'))
      }
    },
    isCommentOwner(comment) {
      const myUserId = String(this.currentUser.userId || '')
      return String(comment.userId || '') === myUserId
    },
    setReplyingTo(chatSessionId, comment) {
      if (this.isCommentOwner(comment)) return
      if (this.commentStates[chatSessionId]) {
        this.$set(this.commentStates[chatSessionId], 'replyingTo', comment)
        this.commentStates[chatSessionId].inputText = ''
      }
    },
    clearReplyingTo(chatSessionId) {
      if (this.commentStates[chatSessionId]) {
        this.$set(this.commentStates[chatSessionId], 'replyingTo', null)
      }
    },
    onCommentLongPress(chatSessionId, comment) {
      if (!this.isCommentOwner(comment)) return
      if (confirm(this.$t('chatSessionComment_deleteConfirm') || 'Delete this comment?')) {
        this.deleteComment(comment.chatSessionCommentId)
      }
    },
    async toggleLike(chatSessionId) {
      if (!this.$store.state.alreadyLogin || !this.$store.state.user.userName) {
        this.$store.state.needLogin = true
        return
      }
      try {
        const response = await axios.post('/api/chat_session_like/toggle', {
          chatSessionId: chatSessionId
        })
        if (response.data.code === 0 && response.data.data) {
          const { liked, likeCnt } = response.data.data
          // 更新本地 session 数据
          const session = this.sessions.find(s => String(s.chatSessionId) === String(chatSessionId))
          if (session) {
            this.$set(session, 'liked', liked)
            this.$set(session, 'likeCnt', likeCnt || 0)
            // 更新 recentLikesJson（乐观更新）
            var myName = this.$store.state.user.userName || ''
            if (liked && myName) {
              var newLikePreview = [{ userName: myName }]
              var oldLikePreviews = this.parseLikePreviews(session.recentLikesJson)
              if (oldLikePreviews.length > 0) {
                for (var i = 0; i < oldLikePreviews.length && newLikePreviews.length < 2; i++) {
                  if (oldLikePreviews[i].userName !== myName) {
                    newLikePreviews.push(oldLikePreviews[i])
                  }
                }
              }
              this.$set(session, 'recentLikesJson', JSON.stringify(newLikePreviews))
            }
          }
        }
      } catch (error) {
        console.error('[ChannelDetail] toggleLike error:', error)
      }
    },
    async toggleBookmark(chatSessionId) {
      if (!this.$store.state.alreadyLogin || !this.$store.state.user.userName) {
        this.$store.state.needLogin = true
        return
      }
      try {
        const response = await axios.post('/api/chat_session_bookmark/toggle', {
          chatSessionId: chatSessionId
        })
        if (response.data.code === 0 && response.data.data) {
          const { bookmarked, bookmarkCnt } = response.data.data
          const session = this.sessions.find(s => String(s.chatSessionId) === String(chatSessionId))
          if (session) {
            this.$set(session, 'bookmarked', bookmarked)
            this.$set(session, 'bookmarkCnt', bookmarkCnt || 0)
          }
        }
      } catch (error) {
        console.error('[ChannelDetail] toggleBookmark error:', error)
      }
    },
    formatReplyTime(ts) {
      if (!ts) return ''
      const date = new Date(ts)
      const mm = (date.getMonth() + 1).toString().padStart(2, '0')
      const dd = date.getDate().toString().padStart(2, '0')
      const hh = date.getHours().toString().padStart(2, '0')
      const min = date.getMinutes().toString().padStart(2, '0')
      return `${mm}/${dd} ${hh}:${min}`
    },

    // ===== Match History =====
    async viewMatchHistory(chatSessionId) {
      this.matchHistoryChatSessionId = chatSessionId
      this.showMatchHistory = true
      this.matchHistoryLoading = true
      this.matchHistoryCurPage = 1
      this.matchHistoryHasMore = false
      this.matchHistoryMatches = []
      this.matchHistoryInterested = []
      this.matchHistoryKnownIds = []
      this.matchHistoryHasNew = false
      this.matchHistoryNewCount = 0
      // 判断是否是自己的 session
      var session = this.sessions.find(function(s) { return s.chatSessionId === chatSessionId })
      var myUserId = String(this.currentUser.userId || '')
      this.isMyMatchSession = session ? String(session.fromUserId || '') === myUserId : true
      this.matchHistoryMatchPaused = session ? Number(session.matchPaused) === 1 : false
      await this._doLoadMatchHistory()
      this.matchHistoryLoading = false
      // 自己的 session 启动持续匹配轮询（已暂停匹配则不轮询）
      this._stopMatchHistoryTimer()
      if (this.isMyMatchSession && !this.matchHistoryMatchPaused) {
        var self = this
        this.matchHistoryTimer = setInterval(function() {
          self._doLoadMatchHistory(true)
        }, 15000)
      }
    },
    _stopMatchHistoryTimer() {
      if (this.matchHistoryTimer) {
        clearInterval(this.matchHistoryTimer)
        this.matchHistoryTimer = null
      }
    },
    async _doLoadMatchHistory(isRefresh) {
      try {
        var response = await axios.post('/api/chat/get_match_history', {
          chatSessionId: this.matchHistoryChatSessionId,
          pageSize: 50,
          curPage: 1
        })
        if (response.data.code === 0 && response.data.data) {
          var details = response.data.data.matchDetails || []
          var allMatches = []
          var allInterested = []
          var newIds = []
          for (var i = 0; i < details.length; i++) {
            var item = details[i]
            if (item.matchLevel === 'matched') {
              allMatches.push(item)
            } else {
              allInterested.push(item)
            }
            var id = item.matchedChatSessionId || ''
            if (id && this.matchHistoryKnownIds.indexOf(id) === -1) {
              newIds.push(id)
            }
          }
          if (isRefresh && newIds.length > 0) {
            this.matchHistoryHasNew = true
            this.matchHistoryNewCount = newIds.length
          }
          this.matchHistoryMatches = allMatches
          this.matchHistoryInterested = allInterested
          this.matchHistoryKnownIds = []
          for (var j = 0; j < allMatches.length; j++) {
            var mid = allMatches[j].matchedChatSessionId || ''
            if (mid) this.matchHistoryKnownIds.push(mid)
          }
          for (var k = 0; k < allInterested.length; k++) {
            var iid = allInterested[k].matchedChatSessionId || ''
            if (iid) this.matchHistoryKnownIds.push(iid)
          }
        }
      } catch (error) {
        console.error('[ChannelDetail] loadMatchHistory error:', error)
      }
    },
    async loadMoreMatchHistory() {
      if (this.matchHistoryLoadingMore || !this.matchHistoryHasMore) return
      this.matchHistoryLoadingMore = true
      this.matchHistoryCurPage++
      try {
        const response = await axios.post('/api/chat/get_match_history', {
          chatSessionId: this.matchHistoryChatSessionId,
          pageSize: 50,
          curPage: this.matchHistoryCurPage
        })
        if (response.data.code === 0 && response.data.data) {
          const details = response.data.data.matchDetails || []
          for (var i = 0; i < details.length; i++) {
            if (details[i].matchLevel === 'matched') {
              this.matchHistoryMatches.push(details[i])
            } else {
              this.matchHistoryInterested.push(details[i])
            }
          }
          this.matchHistoryHasMore = details.length >= 50
        } else {
          this.matchHistoryHasMore = false
        }
      } catch (error) {
        console.error('[ChannelDetail] loadMoreMatchHistory error:', error)
        this.matchHistoryHasMore = false
      } finally {
        this.matchHistoryLoadingMore = false
      }
    },
    onMatchHistoryScroll(e) {
      const el = e.target
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
        this.loadMoreMatchHistory()
      }
    },
    goToMatchedSession(item) {
      const url = `/app/#/matched-session?currentChatSessionId=${this.matchHistoryChatSessionId}&matchedChatSessionId=${item.matchedChatSessionId}`
      if (item.matchDetailId) {
        window.open(url + `&matchDetailId=${item.matchDetailId}`, '_blank')
      } else {
        window.open(url, '_blank')
      }
    },

    copyChannelCode() {
      if (!this.channel.channelCode) return
      // 与 backend-api SSR 分享页口令格式保持一致：[品牌] 邀你加入「群名」+ 口令 + 特点句
      var brand = this.$config && this.$config.region === 'cn' ? '章摇摇' : 'OctoSay'
      var inviteText = '[' + brand + '] ' + this.$t('channel_detail_invitePhrase', {
        name: this.channel.name || '',
        code: this.channel.channelCode
      })
      if (navigator.clipboard) {
        navigator.clipboard.writeText(inviteText)
        Toast.success(this.$t('copy') + ' OK')
      } else {
        const el = document.createElement('textarea')
        el.value = inviteText
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
        Toast.success(this.$t('copy') + ' OK')
      }
    },
    async handleShareChannel() {
      await shareChannel(this.channel, this.$i18n)
    },
    async copyChannelLink() {
      if (!this.channel.channelCode) return
      const result = await copyToClipboard(buildChannelShareUrl(this.channel.channelCode))
      if (result && result.copied) {
        Toast.success(this.$t('toast_link_copied'))
      }
    },
    async handleShareSession(session) {
      await shareSession(session, this.$i18n)
    },
    showQRCode() {
      if (!this.channel.channelCode) return
      this.qrMergedImageUrl = ''
      this.qrMergedCanvas = false
      this.qrMerging = false
      this._qrCanvas = null
      this.showQRPopup = true
      var self = this
      setTimeout(function() {
        self.mergeQRImage()
      }, 600)
    },
    getInviteUrl() {
      if (!this.channel.channelCode) return ''
      var base = window.location.origin
      return base + '/share/channel/' + this.channel.channelCode + '.html'
    },
    mergeQRImage() {
      var container = this.$refs.qrImageContainer
      if (!container) return
      // 检查 vue-qr 是否渲染完成（它渲染为 <img>，src 是 data: URI）
      var qrImgEl = container.querySelector('.vue-qr img') || container.querySelector('img[src^="data:"]')
      if (!qrImgEl) {
        var self = this
        setTimeout(function() { self.mergeQRImage() }, 300)
        return
      }
      this.qrMerging = true
      var self = this
      // allowTaint: true 让 html2canvas 不设 crossOrigin，直接用已加载的图片渲染
      // 这样 DOM 中正常显示的图片在截图中也能正常显示
      // 代价是 canvas 会 tainted，toDataURL 会报错，所以直接展示 canvas 元素
      html2canvas(container, {
        useCORS: false,
        allowTaint: true,
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        // 只忽略二维码容器以外的无关分支，但保留 <head>，否则样式丢失会导致空白
        ignoreElements: function(el) {
          if (el === container || el.contains(container) || container.contains(el)) return false
          if (el.tagName === 'HEAD' || el.tagName === 'HTML' || el.tagName === 'BODY') return false
          return true
        }
      }).then(function(canvas) {
        canvas.style.maxWidth = '100%'
        canvas.style.maxHeight = 'calc(60vh - 200px)'
        canvas.style.width = 'auto'
        canvas.style.height = 'auto'
        canvas.style.borderRadius = '16px'
        canvas.style.display = 'block'
        // 不要存 canvas.outerHTML，那样会丢失已绘制像素，显示一片空白。
        // 用布尔值切换展示容器，在 nextTick 里把真实 canvas 挂载上去。
        self.qrMergedCanvas = true
        // 保存 canvas 引用供下载用
        self._qrCanvas = canvas
        self.qrMerging = false
        // 如果用户在生成过程中关闭了弹窗，就不要再把 canvas 挂载到已关闭的 DOM 里
        if (!self.showQRPopup) return
        self.$nextTick(function() {
          var holder = self.$refs.qrMergedCanvasContainer
          if (holder) {
            holder.innerHTML = ''
            holder.appendChild(canvas)
          }
        })
      }).catch(function(err) {
        console.error('[QR] html2canvas error:', err)
        self.qrMerging = false
      })
    },
    downloadQRImage() {
      // 优先用 canvas 引用下载
      if (this._qrCanvas) {
        try {
          var dataUrl = this._qrCanvas.toDataURL('image/png')
          this._doDownload(dataUrl)
          return
        } catch (e) {
          // tainted canvas，用另一种方式：把 canvas 绘制到新窗口让用户保存
        }
      }
      if (this.qrMergedImageUrl) {
        this._doDownload(this.qrMergedImageUrl)
        return
      }
      // tainted canvas 无法 toDataURL，打开新窗口展示 canvas 让用户右键保存
      if (this._qrCanvas) {
        var w = window.open('')
        if (w) {
          w.document.write('<html><head><title>QR Code</title></head><body style="display:flex;align-items:center;justify-content:center;min-height:100vh;margin:0;background:#F7F5F2;">')
          w.document.write('<div style="text-align:center"><p style="color:#999;font-size:14px;margin-bottom:12px;">Right click the image to save</p>')
          var cloned = this._qrCanvas.cloneNode(true)
          cloned.style.maxWidth = '100%'
          cloned.style.borderRadius = '16px'
          w.document.write(cloned.outerHTML)
          w.document.write('</div></body></html>')
          w.document.close()
        }
      }
    },
    _doDownload(dataUrl) {
      var link = document.createElement('a')
      link.download = 'octopie_qr_' + (this.channel.channelCode || '') + '.png'
      link.href = dataUrl
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      Toast.success(this.$t('channel_detail_qrCodeSaved'))
    },
    parseCommentPreviews(jsonStr) {
      if (!jsonStr) return []
      try {
        var arr = JSON.parse(jsonStr)
        return Array.isArray(arr) ? arr : []
      } catch (e) {
        return []
      }
    },
    parseLikePreviews(jsonStr) {
      if (!jsonStr) return []
      try {
        var arr = JSON.parse(jsonStr)
        return Array.isArray(arr) ? arr : []
      } catch (e) {
        return []
      }
    },
    normalizeJoinMsg(j) {
      return {
        id: String(j.id || ''),
        cnt: Number(j.cnt || 0),
        anchorSessionId: String(j.anchorSessionId || ''),
        anchorPublishedAt: String(j.anchorPublishedAt || ''),
        lastJoinAt: String(j.lastJoinAt || ''),
        users: (j.users || []).map(u => ({
          userId: String(u.userId || ''),
          userName: u.userName || '',
          avatar: u.avatar || ''
        }))
      }
    },
    mergeJoinMsgs(list) {
      // 按 id 归并：已存在的 banner 原地替换（服务端会原地累加 cnt/users），新出现的追加
      const map = {}
      for (const j of this.joinMsgs) { map[j.id] = j }
      for (const raw of (list || [])) {
        const j = this.normalizeJoinMsg(raw)
        if (!j.id) continue
        map[j.id] = j
      }
      this.joinMsgs = Object.keys(map).map(k => map[k])
    },
    joinMsgAvatarUsers(j) {
      // banner 头像簇只展示有真实头像的用户（没头像的不出占位首字母块）
      return ((j && j.users) || []).filter(function (u) { return u && u.avatar })
    },
    joinMsgText(j) {
      const users = (j && j.users) || []
      const cnt = Number(j.cnt) || users.length || 0
      const names = []
      for (const u of users) {
        if (u && u.userName) names.push(u.userName)
      }
      if (names.length === 0) return this.$t('channelJoinMsg_membersJoined', { n: cnt })
      if (cnt <= 1) return this.$t('channelJoinMsg_oneJoined', { name: names[0] })
      if (names.length >= 2) {
        if (cnt == 2) return this.$t('channelJoinMsg_twoJoined', { name1: names[0], name2: names[1] })
        return this.$t('channelJoinMsg_multiJoined', { name1: names[0], name2: names[1], n: cnt - 2 })
      }
      // 仅 1 个用户信息可用但 cnt > 1（其余用户信息缺失），cnt 以服务端为准
      return this.$t('channelJoinMsg_oneAndMoreJoined', { name: names[0], n: cnt - 1 })
    },
    normalizeSession(s) {
      return {
        chatSessionId: String(s.chatSessionId || ''),
        fromUserId: String(s.fromUserId || ''),
        channelId: String(s.channelId || ''),
        aiSummary: s.aiSummary || '',
        requirementDetail: s.requirementDetail || '',
        userMsg: s.userMsg || '',
        fromUserName: s.fromUserName || '',
        fromUserAvatar: s.fromUserAvatar || '',
        public: Number(s.public || 0),
        matchPaused: Number(s.matchPaused || 0),
        contentHidden: !!s.contentHidden,
        createdAt: Number(s.createdAt || 0),

        publishedAt: Number(s.publishedAt || 0),
        status: Number(s.status || 0),
        requirementClarified: Number(s.requirementClarified || 0),
        chatSessionCommentCnt: Number(s.chatSessionCommentCnt || 0),
        matchCnt: Number(s.matchCnt || 0),
        likeCnt: Number(s.likeCnt || 0),
        allowDirectContact: Number(s.allowDirectContact || 0),
        latestCommentsJson: s.latestCommentsJson || '',
        recentLikesJson: s.recentLikesJson || '',
        bookmarkCnt: Number(s.bookmarkCnt || 0),
        bookmarked: s.bookmarked == true,
        firstMediaContent: s.firstMediaContent || '',
      }
    },
    parseFirstMediaContent(str) {
      if (!str) return null
      try {
        var obj = JSON.parse(str)
        if (!obj || !obj.type) return null
        return obj
      } catch (e) {
        return null
      }
    },
    isUserMsgMixed(userMsg) {
      if (!userMsg) return false
      try {
        var parsed = Array.isArray(userMsg) ? userMsg : JSON.parse(userMsg)
        return Array.isArray(parsed) && parsed.length > 0 && parsed[0] && parsed[0].type
      } catch (e) {
        return false
      }
    },
    parseMixedContent(userMsg) {
      if (!userMsg) return []
      try {
        var parsed = Array.isArray(userMsg) ? userMsg : JSON.parse(userMsg)
        if (!Array.isArray(parsed)) return []
        return parsed
      } catch (e) {
        return []
      }
    },
    getStaticMapUrl(lat, lng) {
      if (!lat || !lng) return ''
      var z = 14
      var n = Math.pow(2, z)
      var x = Math.floor((parseFloat(lng) + 180) / 360 * n)
      var latRad = parseFloat(lat) * Math.PI / 180
      var y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n)
      return 'https://b.basemaps.cartocdn.com/light_all/' + z + '/' + x + '/' + y + '.png'
    },
  }
}
</script>

<style scoped>
.channel-detail-page {
  background: #F7F5F2;
  display: flex;
  flex-direction: column;
}

.channel-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: white;
  border: 1px solid #E0DDD8;
  flex-shrink: 0;
}

.channel-detail-title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 60%;
  cursor: pointer;
}

.channel-header-avatar {
  width: 28px;
  height: 28px;
  border-radius: 16%;
  object-fit: cover;
  flex-shrink: 0;
}

.channel-detail-title-group {
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
}

.channel-detail-title {
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.channel-detail-members {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #8A8A8A;
  margin-top: 2px;
}

.channel-desc-banner {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #FFF8F6, #FEF0ED);
  border-bottom: 1px solid #F8A090;
  flex-shrink: 0;
}

.channel-desc-text {
  font-size: 12px;
  color: #8A8A8A;
  line-height: 1.4;
}

.channel-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
}
/* 只有进群 banner、还没有需求卡时：空状态提示与 banner 共存的变体。
   flex:1 吸收 banner 之外的剩余空间并在内部垂直居中（banner 留在时间线顶部） */
.channel-empty-state-inline {
  flex: 1;
  padding: 16px 24px 24px;
}
.channel-empty-icon {
  position: relative;
  width: 80px;
  height: 80px;
}
.channel-empty-icon-bg {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #FFF8F6;
  border: 1px solid #F8A090;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(232, 84, 58, 0.10);
}
.channel-empty-icon-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
  animation: channel-empty-logo-spin 4s linear infinite;
}
@keyframes channel-empty-logo-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.channel-empty-title {
  margin-top: 20px;
  font-size: 17px;
  font-weight: 600;
  color: #1A1A1A;
}
.channel-empty-subtitle {
  margin-top: 8px;
  font-size: 13px;
  color: #8A8A8A;
  text-align: center;
  line-height: 1.5;
}
.channel-empty-hint {
  margin-top: 6px;
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 5px 10px;
  background: #FEF0ED;
  border-radius: 10px;
  font-size: 11px;
  color: #E8543A;
  line-height: 1.4;
}
.channel-empty-hint-icon {
  margin-top: 1px;
  flex-shrink: 0;
}
.channel-empty-members {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  background: #F7F5F2;
  border-radius: 12px;
  font-size: 12px;
  color: #8A8A8A;
}
.channel-empty-action {
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  background: linear-gradient(135deg, #E8543A, #E8543A);
  border-radius: 20px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(248, 160, 144, 0.3);
  transition: opacity 0.2s;
}
.channel-empty-action:hover {
  opacity: 0.9;
}
.channel-empty-action:active {
  opacity: 0.8;
}

.channel-sessions-wrapper {
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.channel-sessions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #ECEAE6;
  flex-shrink: 0;
}

.channel-sessions {
  background: #ECEAE6;
  padding: 0 16px 16px;
  flex: 1;
  overflow-y: auto;
}

/* column-reverse 容器：index 0 在视觉底部，index last 在视觉顶部 */
/* 等同于 Flutter 的 reverse: true，加载更早消息追加到数组末尾 = 视觉顶部，滚动位置自然不变 */
.sessions-reverse {
  display: flex;
  flex-direction: column-reverse;
}

/* 占位元素（DOM 第一个子元素 = 视觉最底部）：内容不足一屏时 auto margin 吸收全部剩余空间，
   把消息整体推到顶部；内容超出一屏时 auto margin 自动为 0，不影响任何滚动/加载逻辑 */
.sessions-bottom-spacer {
  margin-bottom: auto;
}

.session-item {
  width: 100%;
}

.session-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 10px;
}

.session-card {
  position: relative;
  background: #FFFFFF;
  border-radius: 6px;
  padding: 8px 10px;
  flex: 0 0 auto;
  min-width: 0;
  max-width: 75%;
  cursor: pointer;
}

/* 气泡小尾巴：指向头像（微信风格），向泡体叠 1px 保证无缝 */
.session-card::before {
  content: '';
  position: absolute;
  top: 10px;
  left: -4px;
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-right: 6px solid #FFFFFF;
}

.session-highlight .session-card {
  background: rgba(254, 249, 231, 0.5);
}



.session-bubble-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

/* 自己的消息：卡片/内容整体靠右，宽度自适应（微信样式） */
.session-bubble-main-mine {
  align-items: flex-end;
}

.session-mine.session-wrapper {
  flex-direction: row-reverse;
}

.session-mine .session-bubble-content {
  align-items: flex-end;
}

.session-mine .session-bubble-meta {
  justify-content: flex-end;
}

/* 自己的消息：name 贴卡片右侧，time 在 name 外侧 */
.session-mine .session-bubble-meta-group {
  flex-direction: row-reverse;
}

.session-mine .session-bubble-body {
  text-align: right;
}

.session-mine .session-bubble-ai-sub {
  justify-content: flex-end;
}

.session-mine .session-bubble-media {
  justify-content: flex-end;
}

.session-mine .session-card {
  background: #FEF0ED;
}

.session-mine .session-card::before {
  left: auto;
  right: -4px;
  border-right: none;
  border-left: 6px solid #FEF0ED;
}



.session-bubble-avatar {
  flex-shrink: 0;
}

.session-bubble-avatar-img {
  width: 40px;
  height: 40px;
  border-radius: 16%;
  object-fit: cover;
}

.session-bubble-avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 16%;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 500;
}

.session-bubble-content {
  width: 100%;
}

.session-bubble-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 4px;
  width: 100%;
}
.session-bubble-quick-settings {
  cursor: pointer;
  flex-shrink: 0;
}

.session-bubble-meta-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.session-bubble-name {
  font-size: 12px;
  color: #4A4A4A;
}

.session-bubble-time {
  font-size: 11px;
  color: #8A8A8A;
}

.match-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  background: rgba(61, 174, 207, 0.1);
  border: 0.5px solid rgba(61, 174, 207, 0.3);
  border-radius: 12px;
  cursor: pointer;
  flex-shrink: 0;
}

.match-badge span {
  font-size: 11px;
  font-weight: 600;
  color: #2A8FAD;
}

.session-bubble-body {
  /* 微信风格：内容直接贴卡片，不独立成框 */
  text-align: left;
}

.session-bubble-summary {
  font-size: 13px;
  color: #4A4A4A;
  line-height: 1.4;
}

.session-bubble-detail {
  font-size: 11px;
  color: #8A8A8A;
  line-height: 1.4;
  margin-top: 4px;
}

.session-bubble-usermsg-main {
  font-size: 14px;
  color: #1A1A1A;
  line-height: 1.5;
}

.session-bubble-ai-sub {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 0.5px solid rgba(224, 221, 216, 0.5);
}

.session-bubble-ai-sub-label {
  font-size: 10px;
  color: #8A6D3B;
  font-weight: 500;
  flex-shrink: 0;
  line-height: 1.4;
  background: #FEF9E7;
  border-radius: 4px;
  padding: 0 5px;
}

.session-bubble-ai-sub-text {
  font-size: 10px;
  color: #4A4A4A;
  line-height: 1.4;
}

.session-bubble-media {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.session-bubble-media-img {
  width: 60px;
  height: 60px;
  border-radius: 6px;
  object-fit: cover;
}

.session-bubble-media-text {
  font-size: 11px;
  color: #4A4A4A;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.session-bubble-media-location {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 1px solid rgba(224, 221, 216, 0.4);
  overflow: hidden;
  max-width: 140px;
}

.session-bubble-media-map {
  width: 140px;
  height: 60px;
  object-fit: cover;
}

.session-bubble-media-location-info {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 6px;
  font-size: 10px;
  color: #4A4A4A;
  overflow: hidden;
}

.session-bubble-media-location-info span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.session-private-lock {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #8A8A8A;
  font-size: 13px;
  padding: 8px 0;
}

.session-highlight {
  animation: highlight-pulse 2s ease-out;
}

@keyframes highlight-pulse {
  0% { background: #FEF9E7; box-shadow: 0 0 0 2px rgba(250, 224, 138, 0.3); }
  100% { background: #ECEAE6; box-shadow: none; }
}

.session-user-msg {
  font-size: 14px;
  color: #1A1A1A;
  margin-bottom: 6px;
  line-height: 1.4;
}

.session-ai-summary {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 14px;
  color: #1A1A1A;
  margin-bottom: 6px;
  line-height: 1.4;
}

.session-requirement-detail {
  font-size: 13px;
  color: #8A8A8A;
  margin-bottom: 8px;
  line-height: 1.4;
}

.session-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.session-footer-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.session-footer-avatar {
  width: 24px;
  height: 24px;
  border-radius: 16%;
  object-fit: cover;
}

.session-footer-avatar-placeholder {
  width: 24px;
  height: 24px;
  border-radius: 16%;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  font-weight: bold;
}

.session-footer-name {
  font-size: 12px;
  color: #8A8A8A;
}

.session-footer-time {
  font-size: 11px;
  color: #bbb;
  margin-left: 8px;
}

/* ===== 互动区域（点赞预览 + 操作按钮） ===== */
.interaction-section {
  margin-top: 4px;
  padding: 2px 0 0 0;
  width: 100%;
  text-align: left;
}

.interaction-section-mine {
  margin-top: 4px;
  padding: 2px 0 0 0;
  width: 100%;
  text-align: right;
}

.interaction-section .like-preview-bar {
  margin: 0 0 3px 0;
  justify-content: flex-start;
}

.interaction-section-mine .like-preview-bar {
  justify-content: flex-end;
}

.interaction-actions {
  display: inline-flex;
  gap: 2px;
  padding: 0;
}

.interaction-action-btn {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 5px;
  border-radius: 6px;
  font-size: 10px;
  color: #8A8A8A;
  cursor: pointer;
  transition: color 0.2s, background 0.15s;
}

.interaction-action-btn:hover {
  color: #4A4A4A;
  background: rgba(224, 221, 216, 0.25);
}

.interaction-action-btn.active {
  color: #E8543A;
}

.interaction-section-mine .interaction-actions {
  justify-content: flex-end;
}

/* 持续匹配中动效：icon 呼吸 + 涟漪扩散（仅 public 需求） */
.match-live-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 13px;
  height: 13px;
  flex-shrink: 0;
  margin-right: 2px;
}
.match-live-icon .match-live-core {
  position: relative;
  z-index: 1;
  animation: matchLivePulse 1.6s ease-in-out infinite;
}
.match-live-ripple {
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  border: 1px solid rgba(61, 174, 207, 0.7);
  border-radius: 50%;
  opacity: 0;
  animation: matchLiveRipple 1.6s ease-out infinite;
  pointer-events: none;
}
.match-live-ripple2 {
  animation-delay: 0.8s;
}
@keyframes matchLiveRipple {
  0% { transform: scale(0.6); opacity: 0.8; }
  100% { transform: scale(1.9); opacity: 0; }
}
@keyframes matchLivePulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.reply-section {
  /* 白色气泡内部子容器不能用半透明白（会隐形），用实色浅灰 */
  background: #F7F5F2;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #E0DDD8;
  margin-top: 8px;
  text-align: left;
}

.reply-list {
  max-height: 300px;
  overflow-y: auto;
}

.reply-item {
  display: flex;
  align-items: flex-start;
  padding: 4px 0;
  cursor: pointer;
  text-align: left;
}

.reply-avatar {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  margin-top: 1px;
}

.reply-avatar-placeholder {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 9px;
  font-weight: bold;
  flex-shrink: 0;
  margin-top: 1px;
}

.reply-body {
  flex: 1;
  margin-left: 6px;
  font-size: 12px;
  color: #1A1A1A;
  line-height: 1.5;
}

.reply-user {
  font-weight: 500;
}

.reply-to-user {
  color: #E8543A;
  font-size: 11px;
}

.reply-colon {
}

.reply-content {
}

.reply-to-btn {
  font-size: 11px;
  color: #8A8A8A;
  flex-shrink: 0;
  margin-left: 4px;
  margin-top: 2px;
  cursor: pointer;
}

.reply-empty {
  text-align: center;
  padding: 8px;
  color: #8A8A8A;
  font-size: 12px;
}

.reply-load-more {
  text-align: center;
  padding: 6px;
  color: #E8543A;
  font-size: 12px;
  cursor: pointer;
}

.reply-input-area {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  align-items: center;
}

.reply-input-wrapper {
  flex: 1;
  position: relative;
}

.reply-input {
  width: 100%;
  border: 1px solid #E0DDD8;
  border-radius: 16px;
  padding: 8px 28px 8px 10px;
  font-size: 12px;
  outline: none;
  background: white;
  box-sizing: border-box;
  resize: none;
  font-family: inherit;
}

.reply-input:focus {
  border-color: #E8543A;
}

.reply-clear-replying {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
}

.reply-send-btn {
  padding: 8px 12px;
  background: #E8543A;
  border-radius: 16px;
  color: white;
  font-size: 12px;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reply-send-btn-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.channel-settings {
  padding: 16px;
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.settings-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #E0DDD8;
  font-size: 14px;
  cursor: pointer;
}

.settings-leave {
  text-align: center;
  padding: 16px 0 8px;
  font-size: 12px;
  color: #ccc;
  cursor: pointer;
}

.settings-notify-group {
  background: #F0EDEA;
  border-radius: 12px;
  padding: 0 12px;
  margin-top: 8px;
}

.settings-notify-header {
  font-size: 12px;
  color: #8A8A8A;
  padding: 10px 0 4px;
}

.settings-item-no-border {
  border-bottom: none;
}

.settings-channel-code {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: #F0EDEA;
  border-radius: 12px;
  margin-bottom: 16px;
}

.settings-channel-code-icon {
  width: 36px;
  height: 36px;
  background: #FEF0ED;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  color: #aaa;
  flex-shrink: 0;
}

.settings-channel-code-value {
  flex: 1;
  font-size: 22px;
  font-weight: 800;
  color: #1A1A1A;
  letter-spacing: 2px;
}

.settings-channel-code-label {
  font-size: 13px;
  color: #8A8A8A;
  flex-shrink: 0;
}

.channel-bottom-bar {
  flex-shrink: 0;
  padding: 8px 16px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
  background: white;
  border: 1px solid #E0DDD8;
}

.channel-bottom-input-bar {
  position: relative;
  flex-shrink: 0;
  padding: 8px 12px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
  background: white;
  border-top: 1px solid #E0DDD8;
}

.channel-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #F7F5F2;
  border-radius: 22px;
  padding: 6px 6px 6px 14px;
}

.channel-input-field {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 15px;
  color: #1A1A1A;
  line-height: 1.4;
}

.channel-input-field::placeholder {
  color: #8A8A8A;
}

.channel-input-send {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.2s;
}

.channel-input-send:active {
  opacity: 0.85;
}

.channel-input-send-disabled {
  background: #F8A090;
  pointer-events: none;
}
.channel-add-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #F7F5F2;
  margin-right: 8px;
  flex-shrink: 0;
  cursor: pointer;
}
.channel-add-btn .van-icon {
  color: #4A4A4A;
}
/* 输入框上方的图片预览区（与 AI 弹框 ChatBox.image-preview-container 样式一致） */
.channel-image-preview-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  margin-bottom: 8px;
  background: #F0EDEA;
  border-radius: 4px;
}
.channel-image-preview-item {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
}
.channel-image-preview-item .preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.channel-image-preview-item .image-delete-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.channel-image-preview-item .image-delete-btn .van-icon {
  color: white;
}
/* 待上传图片的 loading 叠加层（与 AI 弹框一致） */
.uploading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}
.channel-action-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 100;
}
.channel-action-panel {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background-color: #FAFAF8;
  border-top: 1px solid #E0DDD8;
  padding: 16px 20px;
  display: flex;
  gap: 20px;
  z-index: 101;
}
.channel-action-panel-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}
.channel-action-panel-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background-color: #F7F5F2;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #E0DDD8;
}
.channel-action-panel-icon .van-icon {
  color: #4A4A4A;
}
.channel-action-panel-label {
  font-size: 12px;
  color: #4A4A4A;
}
.channel-action-panel-hint {
  font-size: 10px;
  color: #8A8A8A;
}

.channel-fab {
  position: fixed;
  right: 32px;
  bottom: calc(48px + env(safe-area-inset-bottom));
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #3DAECF;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(61, 174, 207, 0.4);
  cursor: pointer;
  z-index: 100;
  transition: opacity 0.2s;
}
.channel-fab:active {
  opacity: 0.9;
}

/* 私聊弹窗（样式对齐 MatchedSession 的联系方式弹窗） */
.contact-popup {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.contact-popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #E0DDD8;
  flex-shrink: 0;
}

.contact-popup-title {
  font-size: 16px;
  font-weight: 600;
  color: #1A1A1A;
  flex: 1;
}

.contact-chat-box {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.session-detail-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ECEAE6;
}

.session-detail-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  flex-shrink: 0;
  gap: 8px;
}

.session-detail-avatar {
  width: 32px;
  height: 32px;
  border-radius: 16%;
  overflow: hidden;
  flex-shrink: 0;
}

.session-detail-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.session-detail-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #E8543A;
  color: white;
  font-size: 14px;
  font-weight: 600;
}

.session-detail-user-info {
  flex: 1;
  min-width: 0;
}

.session-detail-user-name {
  font-size: 14px;
  font-weight: 500;
  color: #1A1A1A;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}

.session-detail-summary-card {
  padding: 8px 12px;
  flex-shrink: 0;
  text-align: left;
}

.session-detail-summary-inner {
  background: #F7F5F2;
  border: 1px solid #E0DDD8;
  border-radius: 8px;
  padding: 12px;
}

.session-detail-summary-card .session-detail-summary-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.session-detail-usermsg-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.session-detail-usermsg-text {
  font-size: 14px;
  color: #1A1A1A;
  line-height: 1.5;
  flex: 1;
}


.session-detail-ai-icon {
  flex-shrink: 0;
  font-size: 10px;
  line-height: 1.3;
  margin-top: 4px;
}

.session-detail-ai-text {
  font-size: 11px;
  color: #4A4A4A;
  line-height: 1.4;
}

.session-detail-summary-text {
  font-size: 13px;
  color: #1A1A1A;
  line-height: 1.4;
  flex: 1;
}

.session-detail-detail-text {
  font-size: 13px;
  color: #8A8A8A;
  line-height: 1.4;
  flex: 1;
}

.session-detail-summary-divider {
  border-top: 0.5px solid #E0DDD8;
  margin: 4px 0;
}

.session-detail-chat {
  flex: 1;
  overflow: hidden;
}

.match-history-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #F0EDEA;
}

.match-history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #E0DDD8;
  flex-shrink: 0;
}

.match-history-content {
  overflow-y: auto;
  flex: 1;
  -webkit-overflow-scrolling: touch;
}

/* 持续匹配横幅 */
.match-history-banner {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  margin: 12px 16px 8px;
  background: linear-gradient(135deg, #FEF0ED 0%, rgba(248,160,144,0.15) 100%);
  border-radius: 12px;
  border: 0.5px solid rgba(248,160,144,0.3);
}
.match-history-spin-logo {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-right: 12px;
  animation: match-logo-spin 4s linear infinite;
}
.match-history-paused-logo {
  flex-shrink: 0;
  margin-right: 12px;
}
/* 暂停匹配态横幅：置灰 */
.match-history-banner-paused {
  background: linear-gradient(135deg, #F7F5F2 0%, #FAFAF8 100%);
  border: 0.5px solid rgba(224,221,216,0.8);
}
.match-history-banner-paused .match-history-banner-title { color: #4A4A4A; }
.match-history-banner-text { flex: 1; text-align: left; }
.match-history-banner-title { font-size: 13px; font-weight: 600; color: #C93E1A; line-height: 1.4; }
.match-history-banner-subtitle { font-size: 11px; color: #8A8A8A; margin-top: 2px; line-height: 1.4; }

@keyframes match-logo-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.match-history-pulse {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #E8543A;
  flex-shrink: 0;
  margin-left: 8px;
  animation: match-pulse 1.5s ease-in-out infinite;
}
@keyframes match-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(0.8); }
}

/* 分组 badge */
.match-history-group-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  margin: 16px 0 12px;
}

/* 匹配卡片 */
.match-history-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #E0DDD8;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  padding: 16px;
  margin-bottom: 16px;
  cursor: pointer;
  text-align: left;
  transition: box-shadow 0.2s;
}
.match-history-card:active {
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
}
.match-history-card .badge-matched { border: 1.5px solid rgba(232,84,58,0.3); }
.match-history-card .badge-interested { border: 1px solid rgba(245,166,35,0.3); }

.match-history-card-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 10px;
}

.match-history-card-msg {
  font-size: 14px;
  color: #1A1A1A;
  line-height: 1.5;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.match-history-card-ai {
  display: flex;
  align-items: flex-start;
  gap: 3px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 0.5px solid rgba(128,128,128,0.15);
  font-size: 11px;
  color: #8A8A8A;
  line-height: 1.3;
}
.match-history-card-ai .ai-summary-label { flex-shrink: 0; font-size: 10px; color: #E8543A; background: #FEF0ED; padding: 1px 6px; border-radius: 4px; margin-right: 4px; }

.match-history-card-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 11px;
  color: #8A8A8A;
}
.match-history-card-avatar {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.match-history-card-avatar-placeholder {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 9px;
  font-weight: 600;
  flex-shrink: 0;
}
.match-history-card-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.match-history-card-time { font-size: 10px; color: #8A8A8A; flex-shrink: 0; }

.match-history-card-reason {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 8px;
  padding: 6px 8px;
  background: #FEF0ED;
  border-radius: 6px;
  font-size: 10px;
  color: #4A4A4A;
  line-height: 1.3;
}
.match-history-card-reason .reason-tag { flex-shrink: 0; font-size: 10px; color: #C93E1A; background: #fff; padding: 1px 6px; border-radius: 4px; }

/* 空状态 */
.match-history-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  width: 100%;
}
.match-history-empty-icon {
  padding: 16px;
  background: #FEF0ED;
  border-radius: 16%;
  margin-bottom: 16px;
}
.match-history-empty-logo {
  width: 64px;
  height: 64px;
  object-fit: contain;
  animation: match-logo-spin 4s linear infinite;
}
.match-history-empty-title {
  font-size: 16px;
  font-weight: 600;
  color: #1A1A1A;
  margin-bottom: 6px;
  text-align: center;
}
.match-history-empty-hint {
  font-size: 11px;
  color: #E8543A;
  text-align: center;
}

.qr-code-popup {
  padding: 16px;
}

.qr-code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px 12px;
  font-size: 18px;
}

/* ===== 评论预览卡片 ===== */
.comment-preview-box {
  margin-top: 6px;
  padding: 6px;
  background: #F7F5F2;
  border-radius: 6px;
  border: 1px solid #E0DDD8;
  cursor: pointer;
}

.comment-preview-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 6px;
}

.comment-preview-item:last-child {
  margin-bottom: 0;
}

.comment-preview-avatar {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  margin-top: 1px;
}

.comment-preview-avatar-default {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 9px;
  font-weight: bold;
  flex-shrink: 0;
  margin-top: 1px;
}

.comment-preview-body {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.comment-preview-name {
  font-size: 11px;
  font-weight: 600;
  color: #4A4A4A;
  margin-bottom: 1px;
}

.comment-preview-content {
  font-size: 12px;
  color: #1A1A1A;
  line-height: 1.4;
  word-break: break-word;
}

.comment-preview-reply-to {
  color: #E8543A;
  font-size: 12px;
}

.comment-preview-more {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-size: 11px;
  color: #8A8A8A;
  font-weight: 400;
}

/* ===== 最近点赞用户展示 ===== */
.like-preview-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #8A8A8A;
}

.like-preview-names {
  color: #8A8A8A;
}

/* ===== 聚合进群消息 banner（轻量：无卡片边框阴影、无点击交互） ===== */
.join-msg-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 4px 16px;
  margin: 2px 0;
}

.join-msg-avatars {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.join-msg-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #F7F5F2;
  margin-left: -8px;
  box-sizing: border-box;
}

.join-msg-avatar:first-child {
  margin-left: 0;
}

.join-msg-text {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: #8A8A8A;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/ChannelDetail.vue